import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, Target, Calendar, Users, Shield, CheckCircle, AlertCircle, TrendingUp, BookOpen, Brain, Clock } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { getRoadmapPmoState, saveRoadmapPmoState } from '../services/roadmapPmoService';

interface GouvernancePRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// DONNÉES — 10 AXES PRIsME
// ============================================================================
const axesPRIsME = [
  {
    id: 1,
    nom: 'Vision & Stratégie',
    avatars: ['Dany Bourassa', 'Éric Hains'],
    intention: 'Définir, communiquer et maintenir une vision PRIsME claire, cohérente et durable.',
    pourquoiCritique: 'Sans vision claire, PRIsME devient un outil parmi d\'autres au lieu d\'un cadre de transformation.',
    concretementBZ: 'PRIsME est le différenciateur stratégique qui positionne BZ comme leader en excellence TI. Chaque Prime comprend pourquoi PRIsME existe et comment il transforme la valeur livrée.',
    erreursFrequentes: [
      'Confondre PRIsME avec un simple outil',
      'Vision floue ou contradictoire',
      'Désalignement avec les objectifs business',
      'Vision non communiquée aux nouveaux employés'
    ],
    niveaux: [
      { niveau: 0, label: 'Inexistant', description: 'Aucune vision formalisée' },
      { niveau: 1, label: 'POC', description: 'Vision documentée mais peu communiquée' },
      { niveau: 2, label: 'Alignée leaders', description: 'Vision partagée avec les leaders' },
      { niveau: 3, label: 'Intégrée planification', description: 'Vision intégrée dans la planification annuelle' },
      { niveau: 4, label: 'Vision vivante', description: 'Vision régulièrement rafraîchie, guide les décisions' },
      { niveau: 5, label: 'ADN organisationnel', description: 'Vision intégrée à l\'ADN, différenciateur reconnu' }
    ],
    aFaireMaintenant: [
      'Formuler la vision PRIsME en 1 minute',
      'Définir 3 priorités stratégiques non négociables',
      'Refuser tout ce qui n\'aligne pas la vision'
    ],
    preuvesVisibles: [
      'Vision versionnée',
      'Pitch standard',
      'Objectifs reliés aux axes PRIsME'
    ]
  },
  {
    id: 2,
    nom: 'Gouvernance & rôles',
    avatars: ['Dany Bourassa', 'Éric Hains', 'Francis Marquis'],
    intention: 'Structurer les décisions, les rôles et les mécanismes d\'escalade.',
    pourquoiCritique: 'Sans gouvernance claire, PRIsME devient incohérent et énergivore.',
    concretementBZ: 'Comité PRIsME 2×/mois · Dany présent 1×/mois · décisions documentées · rôles clairs.',
    erreursFrequentes: [
      'Gouvernance = bureaucratie',
      'Décisions non documentées',
      'Rôles flous',
      'Mauvaise escalade'
    ],
    niveaux: [
      { niveau: 0, label: 'Ad-hoc', description: 'Décisions ad-hoc, rôles non définis' },
      { niveau: 1, label: 'Informelle', description: 'Quelques personnes prennent des décisions' },
      { niveau: 2, label: 'Comité formé', description: 'Comité PRIsME formé, rôles de base définis' },
      { niveau: 3, label: 'Cadence + décisions', description: 'Cadence régulière, décisions documentées, RACI partiel' },
      { niveau: 4, label: 'RACI + Shield', description: 'RACI complet, escalade claire, lien avec Shield' },
      { niveau: 5, label: 'Auto-gouvernance', description: 'Auto-gouvernance par les Primes, décisions rapides' }
    ],
    aFaireMaintenant: [
      'Charte Comité PRIsME (1 page)',
      'Decision Packet obligatoire',
      'Calendrier annuel'
    ],
    preuvesVisibles: [
      'Registre décisions',
      'RACI affiché',
      'Agenda type'
    ]
  },
  {
    id: 3,
    nom: 'PRIsME Client',
    avatars: ['Éric Hains', 'Francis Marquis', 'Jean-Michel Caouette'],
    intention: 'Livrer un PRIsME client personnalisé, premium et reproductible.',
    pourquoiCritique: 'C\'est le cœur de la valeur perçue par le client.',
    concretementBZ: 'Clonage documenté · personnalisation réelle · QA avant livraison · maintenance continue.',
    erreursFrequentes: [
      'PRIsME générique',
      'Pas de QA',
      'Spécificités client non documentées',
      'Abandon post-livraison'
    ],
    niveaux: [
      { niveau: 0, label: 'Inexistant', description: 'PRIsME Client inexistant ou non fonctionnel' },
      { niveau: 1, label: 'Manuel', description: 'Quelques PRIsME clients créés manuellement' },
      { niveau: 2, label: 'Documenté', description: 'Processus de clonage documenté, personnalisation basique' },
      { niveau: 3, label: 'Reproductible', description: 'Tous les Primes peuvent créer, QA systématique' },
      { niveau: 4, label: 'Automatisé', description: 'Standardisé et automatisé, personnalisation avancée' },
      { niveau: 5, label: 'Référence marché', description: 'Différenciateur concurrentiel, clients demandent PRIsME' }
    ],
    aFaireMaintenant: [
      'Checklist QA (≤20 items)',
      'Pack PME (50 livrables + 20 no-brainers)',
      'Template personnalisation client'
    ],
    preuvesVisibles: [
      'QA sign-off',
      'Procédure clonage',
      'Dossier client'
    ]
  },
  {
    id: 4,
    nom: 'PRIsME Employé et Département',
    avatars: ['Jean-Pierre Leclerc', 'Dany Bourassa'],
    intention: 'Fournir à chaque employé un PRIsME structuré selon son rôle, avec les gestionnaires responsables de leur contenu départemental.',
    pourquoiCritique: 'Onboarding, autonomie, fin des silos. Les gestionnaires sont responsables de leur contenu dans PRIsME Master.',
    concretementBZ: 'Un PRIsME par rôle · source unique de vérité · contenu à jour · gestionnaires responsables de leur département.',
    erreursFrequentes: [
      'Contenu non maintenu',
      'Silos persistants',
      'Pas intégré à l\'onboarding'
    ],
    niveaux: [
      { niveau: 0, label: 'Aucun', description: 'Aucun PRIsME Employé, information dispersée' },
      { niveau: 1, label: 'Partiel', description: 'PRIsME Employé pour quelques rôles clés' },
      { niveau: 2, label: 'Tous rôles', description: 'PRIsME Employé pour tous les rôles, contenu de base' },
      { niveau: 3, label: 'Intégré onboarding', description: 'Intégré à l\'onboarding, mise à jour régulière, gestionnaires commencent à gérer leur contenu' },
      { niveau: 4, label: 'Source unique', description: 'Source unique de vérité, personnalisation avancée, gestionnaires responsables de leur département' },
      { niveau: 5, label: 'ROI mesurable', description: 'Transforme la productivité, adoption universelle, gestionnaires autonomes sur leur contenu' }
    ],
    aFaireMaintenant: [
      '5 rôles prioritaires',
      'Checklist onboarding PRIsME',
      'Gestionnaires responsables de leur contenu départemental',
      'Owner par section/département'
    ],
    preuvesVisibles: [
      'Adoption hebdo',
      'Temps onboarding ↓',
      'Pages versionnées',
      'Gestionnaires actifs sur leur contenu'
    ]
  },
  {
    id: 5,
    nom: 'Innovation & Évolution',
    avatars: ['Dany Bourassa', 'Jean-Michel Caouette'],
    intention: 'Faire évoluer PRIsME sans créer de chaos, en intégrant la sécurité dès la conception.',
    pourquoiCritique: 'Sans innovation contrôlée, PRIsME devient legacy. Sans sécurité intégrée, PRIsME devient vulnérable.',
    concretementBZ: 'ARIA · BOT PRIsME · Gestion documentaire 8 1/2 x 11 impeccable (PDF) · Automatisation · Gestion de projet AI intégré · Sécurité intégrée dès la conception · feedback terrain → roadmap.',
    erreursFrequentes: [
      'Innovation gadget',
      'Pas écouter les utilisateurs',
      'Trop d\'initiatives en parallèle',
      'Documents beaux à l\'écran mais inutilisables en PDF',
      'Automatisation sans gouvernance',
      'Sécurité ajoutée après coup (bolted-on)',
      'Ignorer les risques sécurité dans l\'innovation'
    ],
    niveaux: [
      { niveau: 0, label: 'Statique', description: 'Aucune innovation, PRIsME statique, sécurité non considérée' },
      { niveau: 1, label: 'Tests isolés', description: 'Innovations ponctuelles, expérimentations isolées, sécurité réactive' },
      { niveau: 2, label: 'Roadmap', description: 'Roadmap d\'innovation définie, projets en cours, gestion documentaire PDF fonctionnelle, sécurité considérée' },
      { niveau: 3, label: 'Processus', description: 'Innovation intégrée au processus, retours collectés, automatisation basique, gestion projet AI intégrée, sécurité intégrée' },
      { niveau: 4, label: 'Culture', description: 'Culture d\'innovation, expérimentations régulières, automatisation avancée, documents PDF impeccables, sécurité by design' },
      { niveau: 5, label: 'Leader marché', description: 'PRIsME leader en innovation, avantage concurrentiel, automatisation complète, gestion projet AI mature, sécurité exemplaire' }
    ],
    aFaireMaintenant: [
      'Pipeline idée → test → standard',
      '1 expérimentation/mois max',
      'Post-mortem systématique',
      'Standard documentaire 8 1/2 x 11 PDF impeccable',
      'Roadmap automatisation (guide → automate)',
      'Intégration gestion projet AI dans PRIsME',
      'Sécurité intégrée dès la conception (security by design)',
      'Audit sécurité régulier des innovations'
    ],
    preuvesVisibles: [
      'Roadmap innovation',
      'Log décisions',
      'Adoption réelle',
      'Documents PDF 8 1/2 x 11 impeccables',
      'Automatisations opérationnelles',
      'Gestion projet AI intégrée',
      'Audits sécurité réalisés',
      'Sécurité by design appliquée'
    ]
  },
  {
    id: 6,
    nom: 'Livraison & Exécution',
    avatars: ['Éric Hains', 'Jean-Michel Caouette', 'Francis Marquis'],
    intention: 'Transformer la vision en livraisons fiables et répétables.',
    pourquoiCritique: 'La crédibilité passe par l\'exécution.',
    concretementBZ: 'Processus documentés · délais respectés · QA systématique.',
    erreursFrequentes: [
      'Livrer sans QA',
      'Délais flous',
      'Pas de doc post-livraison'
    ],
    niveaux: [
      { niveau: 0, label: 'Chaotique', description: 'Livraisons chaotiques, non reproductibles' },
      { niveau: 1, label: 'Partiel', description: 'Quelques livraisons réussies, processus partiel' },
      { niveau: 2, label: 'Standard', description: 'Processus standardisé, checklist qualité basique' },
      { niveau: 3, label: 'Reproductible', description: 'Livraisons reproductibles, QA systématique' },
      { niveau: 4, label: 'Optimisé', description: 'Excellence opérationnelle, automatisation partielle' },
      { niveau: 5, label: 'Référence', description: 'Livraison exemplaire, processus optimisés' }
    ],
    aFaireMaintenant: [
      'Definition of Done PRIsME',
      'Checkpoints qualité',
      'Rituel post-delivery'
    ],
    preuvesVisibles: [
      'Délais mesurés',
      'QA pass rate',
      'Rework ↓'
    ]
  },
  {
    id: 7,
    nom: 'Communication & Commercialisation',
    avatars: ['Pierre-Luc Fortin', 'Éric Hains'],
    intention: 'PRIsME permet de rendre l\'expertise TI de BZ compréhensible, désirable et vendable.',
    pourquoiCritique: 'Sans communication, la valeur reste invisible.',
    concretementBZ: 'Stratégie PULL · kit vente · communication interne régulière.',
    erreursFrequentes: [
      'Jargon technique',
      'Oublier l\'interne',
      'Ventes non formées'
    ],
    niveaux: [
      { niveau: 0, label: 'Inexistant', description: 'Aucune communication structurée' },
      { niveau: 1, label: 'Ponctuel', description: 'Communication ponctuelle, présentations internes' },
      { niveau: 2, label: 'Structuré', description: 'Communication régulière interne, arguments vente de base' },
      { niveau: 3, label: 'Formé', description: 'Communication structurée interne/externe, formation commerciale' },
      { niveau: 4, label: 'Standard vente', description: 'PRIsME argument vente standard, cas clients documentés' },
      { niveau: 5, label: 'Référence marché', description: 'PRIsME différenciateur reconnu, clients demandent PRIsME' }
    ],
    aFaireMaintenant: [
      'Kit vente PRIsME',
      'Plan com interne mensuel',
      '3 cas clients avant/après'
    ],
    preuvesVisibles: [
      'Scripts vente',
      'Adoption kit',
      'Pipeline influencé'
    ]
  },
  {
    id: 8,
    nom: 'Qualité, KPI & Suivi',
    avatars: ['Jean-Pierre Leclerc', 'Pierre-Luc Fortin'],
    intention: 'Piloter PRIsME par les données.',
    pourquoiCritique: 'Sans mesure, pas d\'amélioration.',
    concretementBZ: 'KPI adoption · qualité · ROI · dashboards vivants.',
    erreursFrequentes: [
      'Trop de KPI',
      'KPI non actionnables',
      'Données non utilisées'
    ],
    niveaux: [
      { niveau: 0, label: 'Aucun', description: 'Aucun KPI, qualité non mesurée' },
      { niveau: 1, label: 'Manuel', description: 'Quelques métriques collectées manuellement' },
      { niveau: 2, label: 'Base', description: 'KPI de base définis, tableau de bord partiel' },
      { niveau: 3, label: 'Structuré', description: 'KPI structurés, tableau de bord opérationnel' },
      { niveau: 4, label: 'Temps réel', description: 'KPI avancés, dashboards temps réel' },
      { niveau: 5, label: 'Prédictif', description: 'KPI prédictifs, amélioration continue data-driven' }
    ],
    aFaireMaintenant: [
      '10 KPI max',
      'Dashboard unique PRIsME Pulse',
      'Rituel décision mensuel'
    ],
    preuvesVisibles: [
      'Dashboard à jour',
      'Actions prises',
      'Tendances suivies'
    ]
  },
  {
    id: 9,
    nom: 'Formation & Adoption',
    avatars: ['Francis Marquis', 'Pierre-Luc Fortin', 'Jean-Michel Caouette'],
    intention: 'Créer une adoption naturelle et durable.',
    pourquoiCritique: 'Sans adoption, pas de ROI.',
    concretementBZ: 'Parcours Prime · champions PRIsME · adoption mesurée.',
    erreursFrequentes: [
      'Formation one-shot',
      'Doc obsolète',
      'Résistance ignorée'
    ],
    niveaux: [
      { niveau: 0, label: 'Aucune', description: 'Aucune formation, adoption faible' },
      { niveau: 1, label: 'Partielle', description: 'Formation ponctuelle, documentation partielle' },
      { niveau: 2, label: 'Structurée', description: 'Formation structurée pour nouveaux Primes' },
      { niveau: 3, label: 'Continue', description: 'Formation continue, documentation à jour' },
      { niveau: 4, label: 'Culture', description: 'Culture PRIsME, champions identifiés' },
      { niveau: 5, label: 'ADN', description: 'Adoption universelle, PRIsME intégré à l\'ADN' }
    ],
    aFaireMaintenant: [
      'Parcours Prime clair',
      'Champions identifiés',
      'Score adoption par rôle'
    ],
    preuvesVisibles: [
      'Taux complétion',
      'Autonomie Prime',
      'Usage réel'
    ]
  },
  {
    id: 10,
    nom: 'Technologie & Outils (incluant BOT)',
    avatars: ['Jean-Michel Caouette', 'Dany Bourassa'],
    intention: 'Assurer une plateforme performante, stable et évolutive.',
    pourquoiCritique: 'La technologie est l\'infrastructure de PRIsME.',
    concretementBZ: 'Stack moderne · BOT PRIsME · ARIA · Outils Charge de projet IA intégré · performance surveillée.',
    erreursFrequentes: [
      'Stack obsolète',
      'Complexité inutile',
      'Performance ignorée'
    ],
    niveaux: [
      { niveau: 0, label: 'Instable', description: 'Stack obsolète ou instable, outils non intégrés' },
      { niveau: 1, label: 'Basique', description: 'Stack de base fonctionnelle, quelques outils intégrés' },
      { niveau: 2, label: 'Moderne', description: 'Stack moderne, BOT PRIsME opérationnel' },
      { niveau: 3, label: 'Optimisée', description: 'Stack optimisée, ARIA intégré, Outils Charge de projet IA intégré' },
      { niveau: 4, label: 'Référence', description: 'Excellence technique, stack de référence, outils IA matures' },
      { niveau: 5, label: 'Leader marché', description: 'Stack leader, BOT et ARIA différenciateurs, Outils Charge de projet IA intégré opérationnel' }
    ],
    aFaireMaintenant: [
      'SLO/SLA internes',
      'Roadmap BOT (guide → automate)',
      'Catalogue actifs + recherche',
      'Intégration Outils Charge de projet IA'
    ],
    preuvesVisibles: [
      'Monitoring',
      'Incidents ↓',
      'BOT utilisé',
      'Outils Charge de projet IA intégré opérationnel'
    ]
  }
];

// ============================================================================
// DONNÉES — MATURITÉ ACTUELLE ET CIBLE
// ============================================================================
const maturiteData = [
  { axe: 'Vision & Stratégie', actuel: 2, cible: 4 },
  { axe: 'Gouvernance & rôles', actuel: 2, cible: 4 },
  { axe: 'PRIsME Client', actuel: 3, cible: 5 },
  { axe: 'PRIsME Employé et Département', actuel: 1, cible: 4 },
  { axe: 'Innovation & Évolution', actuel: 2, cible: 4 },
  { axe: 'Livraison & Exécution', actuel: 3, cible: 5 },
  { axe: 'Communication & Commercialisation', actuel: 1, cible: 3 },
  { axe: 'Qualité, KPI & Suivi', actuel: 1, cible: 3 },
  { axe: 'Formation & Adoption', actuel: 2, cible: 4 },
  { axe: 'Technologie & Outils', actuel: 3, cible: 5 }
];

// ============================================================================
// DONNÉES — RACI
// ============================================================================
const raciData = [
  {
    activite: 'Vision & Stratégie PRIsME',
    roles: {
      'Dany Bourassa': 'A',
      'Éric Hains': 'C',
      'Comité PRIsME': 'C'
    }
  },
  {
    activite: 'Gouvernance & Décisions',
    roles: {
      'Dany Bourassa': 'A',
      'Éric Hains': 'R',
      'Francis Marquis': 'C',
      'Comité PRIsME': 'I'
    }
  },
  {
    activite: 'Livraison PRIsME Client',
    roles: {
      'Francis Marquis': 'R',
      'Jean-Michel Caouette': 'R',
      'Pierre-Luc Fortin': 'R',
      'Directeurs de compte': 'C',
      'Primes': 'C',
      'Éric Hains': 'A',
      'Comité PRIsME': 'I'
    }
  },
  {
    activite: 'PRIsME Employé et Département',
    roles: {
      'Jean-Pierre Leclerc': 'A',
      'Gestionnaire': 'R',
      'Dany Bourassa': 'C',
      'Comité PRIsME': 'C'
    }
  },
  {
    activite: 'Qualité, KPI & Suivi',
    roles: {
      'Éric Hains': 'C',
      'Jean-Pierre Leclerc': 'A',
      'Pierre-Luc Fortin': 'R',
      'Comité PRIsME': 'C',
      'Dany Bourassa': 'I'
    }
  },
  {
    activite: 'Innovation & Évolution',
    roles: {
      'Dany Bourassa': 'A',
      'Jean-Michel Caouette': 'R',
      'Comité PRIsME': 'C'
    }
  },
  {
    activite: 'Communication Interne',
    roles: {
      'Francis Marquis': 'A',
      'Pierre-Luc Fortin': 'R',
      'Éric Hains': 'C',
      'Comité PRIsME': 'C',
      'Dany Bourassa': 'I'
    }
  },
  {
    activite: 'Commercialisation PRIsME',
    roles: {
      'Francis Marquis': 'A',
      'Pierre-Luc Fortin': 'R',
      'Directeurs de compte': 'I',
      'Éric Hains': 'C',
      'Comité PRIsME': 'C',
      'Gestionnaire': 'I'
    }
  },
  {
    activite: 'Formation & Adoption',
    roles: {
      'Éric Hains': 'A',
      'Pierre-Luc Fortin': 'R',
      'Jean-Michel Caouette': 'R',
      'Francis Marquis': 'R',
      'Comité PRIsME': 'C'
    }
  },
  {
    activite: 'Technologie & Outils (BOT)',
    roles: {
      'Éric Hains': 'A',
      'Jean-Michel Caouette': 'R',
      'Dany Bourassa': 'C',
      'Comité PRIsME': 'C'
    }
  },
  {
    activite: 'Gestion documentaire PRIsME',
    roles: {
      'Francis Marquis': 'A',
      'Jean-Michel Caouette': 'R',
      'Pierre-Luc Fortin': 'C',
      'Dany Bourassa': 'C',
      'Comité PRIsME': 'I'
    }
  }
];

const avatarMap: Record<string, string> = {
  'Dany Bourassa': '/DB.png',
  'Éric Hains': '/EH.png',
  'Francis Marquis': '/FM.png',
  'Jean-Michel Caouette': '/JMC.png',
  'Jean-Pierre Leclerc': '/JP.png',
  'Pierre-Luc Fortin': '/PLF.png',
  'Gestionnaire': '👤', // Special case - will render as emoji
  'Comité PRIsME': '/LogoPrismeSmall.png',
  'Primes': '/BZ.png',
  'Directeurs de compte': '$' // Special case - will render as symbol
};

// ============================================================================
// DONNÉES — ROADMAP 2026
// ============================================================================
const roadmap2026 = [
  {
    trimestre: 'Q1 2026',
    intention: 'Fondations opérationnelles — Déploiement PRIsME Employé et intégration gestion de projets',
    axes: [
      {
        axe: 'PRIsME Employé et Département',
        intention: 'Déployer PRIsME Employé pour tous les rôles et responsabiliser les gestionnaires sur leur contenu départemental',
        livrables: [
          { texte: 'Déploiement PRIsME Employé pour 50% des employés', effort: 40, priorite: 'Haute' },
          { texte: 'Onboarding intégré à PRIsME Employé avec checklist par rôle', effort: 20, priorite: 'Haute' },
          { texte: 'Documentation processus par rôle (responsabilités, livrables, outils)', effort: 30, priorite: 'Haute' },
          { texte: 'Formation gestionnaires sur leur responsabilité contenu départemental', effort: 15, priorite: 'Moyenne' },
          { texte: 'Pages PRIsME Employé versionnées et mises à jour régulièrement', effort: 10, priorite: 'Moyenne' },
          { texte: 'Métriques adoption PRIsME Employé (visites, temps onboarding, autonomie)', effort: 15, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Livraison & Exécution',
        intention: 'Augmenter livraisons PRIsME Client (15→50) et cloner TactiCE (Projet) dans PRIsME',
        livrables: [
          { texte: 'Augmenter livraisons PRIsME Client de 15 à 50 livrables', effort: 40, priorite: 'Haute' },
          { texte: 'Cloner TactiCE (Projet) dans PRIsME avec structure complète', effort: 50, priorite: 'Haute' },
          { texte: 'Gouvernance projet structurée (rôles, décisions, escalade) - pilotée par DB et EH', effort: 25, priorite: 'Haute' },
          { texte: 'Intégration projets TactiCE avec PRIsME Client (livraison traçable)', effort: 30, priorite: 'Haute' },
          { texte: 'Visibilité risques/avancement/valeur livrée par projet', effort: 20, priorite: 'Moyenne' },
          { texte: 'Templates projets PRIsME (kickoff, suivi, rétrospective)', effort: 15, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Technologie & Outils (BOT)',
        intention: 'Rendre le BOT PRIsME opérationnel pour automatiser les tâches répétitives',
        livrables: [
          { texte: 'BOT PRIsME opérationnel (réponses FAQ, guidage processus)', effort: 50, priorite: 'Haute' },
          { texte: 'Automatisation tâches répétitives (création pages, notifications, rappels)', effort: 40, priorite: 'Haute' },
          { texte: 'Intégration workflows PRIsME (escalade, approbations, suivi)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Interface conversationnelle pour guider les Primes', effort: 25, priorite: 'Moyenne' },
          { texte: 'Analytics utilisation BOT (questions fréquentes, points de friction)', effort: 15, priorite: 'Basse' }
        ]
      },
      {
        axe: 'Innovation & Évolution',
        intention: 'Créer le catalogue d\'actifs et améliorer la gestion documentaire',
        livrables: [
          { texte: 'Catalogue d\'actifs TI complet (inventaire, classification, tagging)', effort: 40, priorite: 'Moyenne' },
          { texte: 'Interface de recherche avancée catalogue (filtres, tags, relations)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Gestion documentaire 8 1/2 x 11 impeccable (PDF export optimisé)', effort: 25, priorite: 'Haute' },
          { texte: 'Automatisation génération documents (rapports, livrables)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Sécurité intégrée (classification, accès, audit)', effort: 20, priorite: 'Haute' }
        ]
      }
    ]
  },
  {
    trimestre: 'Q2 2026',
    intention: 'Structuration gouvernance et mesure — Formaliser les processus et mesurer l\'impact',
    axes: [
      {
        axe: 'Gouvernance & Décisions',
        intention: 'Formaliser la gouvernance PRIsME avec processus décision clairs et lien avec Shield',
        livrables: [
          { texte: 'RACI complet documenté et visible dans PRIsME', effort: 10, priorite: 'Haute' },
          { texte: 'Processus décision formalisé (critères, escalade, délais)', effort: 20, priorite: 'Haute' },
          { texte: 'Lien avec Shield / Decision Desk opérationnel (firewall mental)', effort: 15, priorite: 'Moyenne' },
          { texte: 'Format Decision Packet standardisé pour escalade vers Dany', effort: 10, priorite: 'Moyenne' },
          { texte: 'Rituels gouvernance documentés (2 réunions/mois, présence Dany 1x)', effort: 8, priorite: 'Basse' },
          { texte: 'Règles d\'escalade claires (quand escalader, quand non)', effort: 12, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Qualité, KPI & Suivi',
        intention: 'Mettre en place les tableaux de bord KPI et métriques qualité automatisées',
        livrables: [
          { texte: 'Tableaux de bord KPI opérationnels (adoption, qualité, valeur livrée)', effort: 40, priorite: 'Haute' },
          { texte: 'Métriques qualité automatisées (conformité PRIsME, satisfaction client)', effort: 30, priorite: 'Haute' },
          { texte: 'Rapports réguliers au Comité PRIsME (mensuel, trimestriel)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Dashboard temps réel maturité PRIsME (spider chart dynamique)', effort: 35, priorite: 'Moyenne' },
          { texte: 'KPI PRIsME Employé (adoption, autonomie, temps onboarding)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Métriques projets (livraison dans délais, budget, qualité)', effort: 30, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Formation & Adoption',
        intention: 'Structurer le programme de formation et identifier les champions PRIsME',
        livrables: [
          { texte: 'Programme formation structuré (onboarding, avancé, spécialisé)', effort: 50, priorite: 'Haute' },
          { texte: 'Champions PRIsME identifiés et formés (1 par département)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Mesure adoption mise en place (taux utilisation, compétences)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Parcours formation par rôle (Prime, Chef projet, Gestionnaire)', effort: 40, priorite: 'Haute' },
          { texte: 'Certification PRIsME interne (niveaux 1, 2, 3)', effort: 30, priorite: 'Basse' },
          { texte: 'Bibliothèque ressources formation (vidéos, guides, templates)', effort: 35, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'PRIsME Employé et Département',
        intention: 'Atteindre adoption universelle et autonomie gestionnaires',
        livrables: [
          { texte: 'Tous les rôles ont leur PRIsME Employé complet et à jour', effort: 30, priorite: 'Haute' },
          { texte: 'Gestionnaires autonomes sur leur contenu départemental', effort: 20, priorite: 'Haute' },
          { texte: 'Mise à jour régulière PRIsME Employé (mensuelle minimum)', effort: 15, priorite: 'Moyenne' },
          { texte: 'Feedback boucle intégrée (suggestions, améliorations)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Intégration PRIsME Employé avec outils quotidiens (SSO, notifications)', effort: 25, priorite: 'Basse' }
        ]
      }
    ]
  },
  {
    trimestre: 'Q3 2026',
    intention: 'Communication et commercialisation — Positionner PRIsME comme différenciateur stratégique',
    axes: [
      {
        axe: 'Communication Interne',
        intention: 'Améliorer la communication interne PRIsME et l\'engagement des équipes',
        livrables: [
          { texte: 'Stratégie communication interne PRIsME (fréquence, canaux, messages)', effort: 15, priorite: 'Moyenne' },
          { texte: 'Newsletter PRIsME mensuelle (succès, nouveautés, formations)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Champions PRIsME actifs dans communication (relais, témoignages)', effort: 15, priorite: 'Basse' },
          { texte: 'Intranet PRIsME enrichi (actualités, ressources, FAQ)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Sessions partage d\'expérience (retours terrain, bonnes pratiques)', effort: 20, priorite: 'Basse' },
          { texte: 'Métriques engagement communication (ouverture, clics, participation)', effort: 15, priorite: 'Basse' }
        ]
      },
      {
        axe: 'Commercialisation PRIsME',
        intention: 'Développer les arguments de vente PRIsME et documenter les cas clients',
        livrables: [
          { texte: 'Arguments de vente PRIsME standardisés (value proposition, différenciateurs)', effort: 25, priorite: 'Haute' },
          { texte: 'Cas clients documentés (avant/après, ROI, témoignages)', effort: 40, priorite: 'Haute' },
          { texte: 'Formation équipes commerciales sur PRIsME (pitch, objections)', effort: 30, priorite: 'Haute' },
          { texte: 'Matériel commercial PRIsME (présentations, one-pagers, démos)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Stratégie PULL commercialisation (contenu, webinaires, événements)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Suivi opportunités PRIsME (pipeline, conversion, valeur)', effort: 20, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Livraison PRIsME Client',
        intention: 'Optimiser le processus de livraison PRIsME Client et garantir qualité 100%',
        livrables: [
          { texte: 'Processus livraison PRIsME Client optimisé (workflow, checkpoints)', effort: 30, priorite: 'Haute' },
          { texte: 'Qualité 100% vérifiée avant livraison (checklist, validation)', effort: 25, priorite: 'Haute' },
          { texte: 'Templates livraison PRIsME Client standardisés (structure, contenu)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Formation équipes livraison (processus, qualité, client)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Métriques livraison (temps, qualité, satisfaction client)', effort: 20, priorite: 'Moyenne' },
          { texte: 'Amélioration continue processus (rétrospectives, ajustements)', effort: 15, priorite: 'Basse' }
        ]
      },
      {
        axe: 'Innovation & Évolution',
        intention: 'Préparer l\'évolution PRIsME et intégrer nouvelles fonctionnalités',
        livrables: [
          { texte: 'Roadmap 2027 préparée (priorités, intentions, livrables)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Expérimentations nouvelles fonctionnalités (POC, validations)', effort: 40, priorite: 'Basse' },
          { texte: 'Intégration ARIA dans PRIsME (si pertinent)', effort: 50, priorite: 'Basse' },
          { texte: 'Amélioration continue basée sur feedback (équipes, clients)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Veille technologique PRIsME (outils, méthodes, tendances)', effort: 20, priorite: 'Basse' },
          { texte: 'Évolution architecture PRIsME (scalabilité, performance)', effort: 45, priorite: 'Moyenne' }
        ]
      }
    ]
  },
  {
    trimestre: 'Q4 2026',
    intention: 'Maturité et vision 2027 — Atteindre niveau 4 et préparer l\'avenir',
    axes: [
      {
        axe: 'Vision & Stratégie PRIsME',
        intention: 'Définir la vision 2027 et aligner stratégie PRIsME avec objectifs BZ',
        livrables: [
          { texte: 'Vision 2027 PRIsME définie (objectifs, axes, maturité cible)', effort: 40, priorite: 'Haute' },
          { texte: 'Alignement stratégique complet (PRIsME ↔ objectifs BZ)', effort: 25, priorite: 'Haute' },
          { texte: 'PRIsME comme différenciateur reconnu (interne et externe)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Positionnement marché PRIsME (concurrents, avantages, opportunités)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Stratégie croissance PRIsME (scalabilité, nouveaux marchés)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Documentation vision stratégique (présentation, plan, communication)', effort: 25, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Technologie & Outils (BOT)',
        intention: 'Optimiser la stack technologique et intégrations avancées',
        livrables: [
          { texte: 'Stack optimisée et performante (performance, scalabilité)', effort: 50, priorite: 'Moyenne' },
          { texte: 'Intégrations avancées (outils quotidiens, systèmes clients)', effort: 60, priorite: 'Moyenne' },
          { texte: 'Performance exemplaire (temps réponse, disponibilité)', effort: 40, priorite: 'Haute' },
          { texte: 'BOT PRIsME avancé (apprentissage, personnalisation)', effort: 55, priorite: 'Basse' },
          { texte: 'Outils Charge de projet IA intégré (estimation, planification)', effort: 70, priorite: 'Moyenne' },
          { texte: 'Monitoring et alertes proactives (santé système, anomalies)', effort: 35, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Maturité globale',
        intention: 'Atteindre niveau 4 sur axes critiques et démontrer ROI mesurable',
        livrables: [
          { texte: 'Niveau 4 atteint sur axes critiques (PRIsME Client, Livraison, Technologie)', effort: 0, priorite: 'Haute' },
          { texte: 'ROI mesurable et documenté (temps économisé, qualité améliorée, valeur)', effort: 40, priorite: 'Haute' },
          { texte: 'PRIsME référence marché (cas clients, témoignages, reconnaissance)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Audit maturité PRIsME complet (10 axes, gaps, plan amélioration)', effort: 30, priorite: 'Moyenne' },
          { texte: 'Rapport annuel PRIsME 2026 (succès, métriques, vision 2027)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Célébration succès PRIsME (équipe, clients, partenaires)', effort: 10, priorite: 'Basse' }
        ]
      },
      {
        axe: 'Gestion documentaire PRIsME',
        intention: 'Finaliser la gestion documentaire impeccable et automatisation',
        livrables: [
          { texte: 'Export PDF 8 1/2 x 11 impeccable pour tous documents PRIsME', effort: 30, priorite: 'Haute' },
          { texte: 'Automatisation complète génération documents (rapports, livrables)', effort: 45, priorite: 'Moyenne' },
          { texte: 'Gestion version documents (historique, comparaison, rollback)', effort: 35, priorite: 'Moyenne' },
          { texte: 'Templates documents standardisés (PRIsME Client, Employé, Projets)', effort: 25, priorite: 'Moyenne' },
          { texte: 'Archivage intelligent documents (rétention, classification)', effort: 30, priorite: 'Basse' },
          { texte: 'Recherche documentaire avancée (full-text, tags, métadonnées)', effort: 40, priorite: 'Basse' }
        ]
      }
    ]
  }
];

type RoadmapOperationnelAxeId =
  | 'PRISME_CLIENT_LIVRAISON'
  | 'CONTENU_METHODE'
  | 'VENTE_COMMUNICATION'
  | 'ADOPTION_FORMATION'
  | 'GOUVERNANCE_QUALITE'
  | 'IA_BOT'
  | 'GOUVERNANCE_SUIVI'
  | 'PRISME_CLIENT_MONTEE'
  | 'PRISME_EMPLOYE_METHODE'
  | 'ADOPTION_EXPERIENCE_CLIENT'
  | 'VENTE_POSITIONNEMENT'
  | 'INTERNE_FORMATION'
  | 'IA_BOT_SPIDER'
  | 'QUALITE_CERTIFICATION'
  | 'GOUVERNANCE_STRATEGIE'
  | 'VENTE_IMAGE';

type RoadmapOperationnelTask = {
  tache: string;
  r: string;
  a: string;
  heures: number;
  jalon: string;
  critere: string;
  // Avancement (0-100) du jalon associé à cette tâche (PMO).
  // Persisté en base de données (partagé).
  avancementPct?: number;
};

type RoadmapOperationnelAxe = {
  id: RoadmapOperationnelAxeId;
  nom: string;
  tasks: RoadmapOperationnelTask[];
};

type RoadmapOperationnelQuarter = {
  trimestre: 'Q1 2026' | 'Q2 2026' | 'Q3 2026' | 'Q4 2026';
  theme: string;
  axes: RoadmapOperationnelAxe[];
  totalHeures?: number;
  synthese?: string[];
};

// ============================================================================
// DONNÉES — ROADMAP OPÉRATIONNELLE 2026 (EXÉCUTION)
// Basée sur la version préparée par l’équipe (PMO).
// ============================================================================
const roadmapOperationnel2026: RoadmapOperationnelQuarter[] = [
  {
    trimestre: 'Q1 2026',
    theme: 'Fondations & mise en cadence',
    axes: [
      {
        id: 'PRISME_CLIENT_LIVRAISON',
        nom: 'Axe 1 — PRISME Client & livraison',
        tasks: [
          { tache: '15 → 50 clients PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 60, jalon: '50 clients actifs', critere: 'Clients visibles + comité actif' },
          { tache: 'R2 / documents (20 clients)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 45, jalon: '20 clients outillés', critere: 'Docs utilisés en comité' },
          { tache: 'Rodage PRISME client', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 30, jalon: 'Process rodé', critere: '< 20 % reprises' },
          { tache: "Catalogue d'actifs PRISME", r: 'Jean-Michel', a: 'Éric', heures: 25, jalon: 'Catalogue actif', critere: 'API dans PRISME' }
        ]
      },
      {
        id: 'CONTENU_METHODE',
        nom: 'Axe 2 — Contenu & méthode PRISME',
        tasks: [
          { tache: 'TACTICE intégré', r: 'Dany / Jean-Michel', a: 'Éric', heures: 30, jalon: 'TACTICE live', critere: 'Onglet Projet créé et 90% fonctionnel' },
          { tache: '10 ODS livrés', r: 'Pierre-Luc', a: 'Francis', heures: 35, jalon: '10 ODS', critere: 'Utilisés en comité' },
          { tache: 'PRISME Employé V1 (60 %)', r: 'Jean-Pierre', a: 'Jean-Pierre', heures: 40, jalon: 'V1 livrée', critere: 'Accès OK' },
          { tache: 'PRISME Département V1', r: 'Jean-Michel', a: 'Dany', heures: 25, jalon: 'V1 livrée', critere: 'Structure validée' }
        ]
      },
      {
        id: 'VENTE_COMMUNICATION',
        nom: 'Axe 3 — Vente & communication',
        tasks: [
          { tache: '10 ventes PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 35, jalon: '10 ventes', critere: 'Opportunités fermées' },
          { tache: 'Adoption ODS vente (50 %)', r: 'Pierre-Luc', a: 'Francis', heures: 20, jalon: '50 % équipe', critere: 'ODS utilisés' },
          { tache: 'Plan communication PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 15, jalon: 'Plan validé', critere: 'Plan appliqué' },
          { tache: '1re communication PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 10, jalon: 'Communication faite', critere: 'Présentation utilisée' }
        ]
      },
      {
        id: 'ADOPTION_FORMATION',
        nom: 'Axe 4 — Adoption interne & formation',
        tasks: [
          { tache: 'Présentation PRISME interne', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 10, jalon: 'Présentation faite', critere: 'Feedback reçu' },
          { tache: 'Formation interne PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 15, jalon: 'Formation livrée', critere: 'Équipe formée' },
          { tache: 'Adoption interne PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 20, jalon: 'Adoption enclenchée', critere: 'Usage réel' }
        ]
      },
      {
        id: 'GOUVERNANCE_QUALITE',
        nom: 'Axe 5 — Gouvernance & qualité',
        tasks: [
          { tache: 'Cadence comités PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 15, jalon: 'Rituels en place', critere: 'Comités tenus' },
          { tache: 'Qualité documents (rodage)', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 15, jalon: 'Critères définis', critere: 'Check qualité' },
          { tache: 'Suivi avancement Q1', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 10, jalon: 'Tableau suivi', critere: 'Avancement mesuré' }
        ]
      }
    ],
    totalHeures: 470,
    synthese: ['Fondations solides et crédibilité', 'PRISME fonctionne pour vrai']
  },
  {
    trimestre: 'Q2 2026',
    theme: 'Industrialisation & stabilisation',
    axes: [
      {
        id: 'PRISME_CLIENT_MONTEE',
        nom: 'Axe 1 — PRISME Client & montée en charge',
        tasks: [
          { tache: '50 → 80 clients PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 70, jalon: '80 clients actifs', critere: 'Clients visibles' },
          { tache: 'Process client stabilisé', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 40, jalon: 'Stabilité atteinte', critere: '< 10 % reprises' },
          { tache: '65 documents livrés', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 35, jalon: '65 docs', critere: 'Docs utilisés' }
        ]
      },
      {
        id: 'PRISME_EMPLOYE_METHODE',
        nom: 'Axe 2 — PRISME Employé & méthode',
        tasks: [
          { tache: 'PRISME Employé complété (100 %)', r: 'Gestionnaires', a: 'Jean-Pierre', heures: 45, jalon: 'Complet', critere: 'Contenu accessible' },
          { tache: 'Adoption PRISME Employé (80 %)', r: 'Gestionnaires', a: 'Jean-Pierre', heures: 30, jalon: '80 % employés', critere: 'Usage réel' },
          { tache: 'Gestion droits & accès', r: 'Gestionnaires', a: 'Jean-Pierre', heures: 20, jalon: 'Accès gérés', critere: 'Sécurisé' }
        ]
      },
      {
        id: 'IA_BOT',
        nom: 'Axe 3 — IA & BOT',
        tasks: [
          { tache: 'BOT PRISME V1', r: 'Jean-Michel', a: 'Dany', heures: 30, jalon: 'BOT live', critere: 'Réponses fonctionnelles' },
          { tache: 'Suivi projet IA', r: 'Jean-Michel', a: 'Éric', heures: 20, jalon: 'Suivi actif', critere: 'Avancement documenté' },
          { tache: 'Qualité réponses BOT', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 15, jalon: 'Qualité mesurée', critere: '> 70 % utiles' }
        ]
      },
      {
        id: 'VENTE_COMMUNICATION',
        nom: 'Axe 4 — Vente & communication',
        tasks: [
          { tache: 'Cadre vente PRISME unifié', r: 'Pierre-Luc', a: 'Francis', heures: 25, jalon: 'Discours unifié', critere: 'Utilisé vente' },
          { tache: 'Formation vente PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 20, jalon: 'Formation livrée', critere: 'Équipe formée' },
          { tache: 'Onglet réunion PRISME amélioré', r: 'Pierre-Luc', a: 'Francis', heures: 15, jalon: 'Onglet prêt', critere: 'Utilisation terrain' }
        ]
      },
      {
        id: 'GOUVERNANCE_SUIVI',
        nom: 'Axe 5 — Gouvernance & suivi',
        tasks: [
          { tache: 'KPI PRISME définis', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 20, jalon: 'KPI validés', critere: 'Utilisés comité' },
          { tache: 'Suivi adoption & performance', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 15, jalon: 'Tableau actif', critere: 'Données à jour' },
          { tache: 'Cadence comités stabilisée', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 15, jalon: 'Cadence stable', critere: 'Sans friction' }
        ]
      }
    ],
    totalHeures: 470,
    synthese: ['PRISME industrialisé et stable', 'Moins de dépendance au hero mode']
  },
  {
    trimestre: 'Q3 2026',
    theme: 'Maturité & création de valeur',
    axes: [
      {
        id: 'PRISME_CLIENT_MONTEE',
        nom: 'Axe 1 — PRISME Client & maturité',
        tasks: [
          { tache: '80 → 120 clients PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 80, jalon: '120 clients actifs', critere: 'Clients visibles' },
          { tache: '+100 documents livrés', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 50, jalon: '+100 docs', critere: 'Utilisés en comité' },
          { tache: '100 % ODS via PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 30, jalon: 'Tous ODS', critere: 'Aucun hors PRISME' }
        ]
      },
      {
        id: 'ADOPTION_EXPERIENCE_CLIENT',
        nom: 'Axe 2 — Adoption & expérience client',
        tasks: [
          { tache: 'Adoption SPIDER (30 % clients)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 35, jalon: '30 % clients', critere: 'Utilisé en comité' },
          { tache: 'Qualité comités TI (90 %)', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 20, jalon: '90 % efficaces', critere: 'Feedback positif' },
          { tache: 'Adoption cadre BZ (30 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 30, jalon: '30 % clients', critere: 'Cadre utilisé' }
        ]
      },
      {
        id: 'IA_BOT',
        nom: 'Axe 3 — IA & innovation',
        tasks: [
          { tache: 'Qualité BOT IA (90 %)', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 25, jalon: '90 % utiles', critere: 'Mesure qualité' },
          { tache: 'Adoption BOT (50 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 30, jalon: '50 % employés', critere: 'Usage réel' },
          { tache: 'Optimisation BOT', r: 'Jean-Michel', a: 'Éric', heures: 20, jalon: 'BOT optimisé', critere: 'Moins erreurs' }
        ]
      },
      {
        id: 'VENTE_POSITIONNEMENT',
        nom: 'Axe 4 — Vente & positionnement',
        tasks: [
          { tache: 'Commercialisation PRISME V1', r: 'Pierre-Luc', a: 'Francis', heures: 30, jalon: 'Offre prête', critere: 'Utilisée vente' },
          { tache: 'Packaging grands comptes', r: 'Pierre-Luc', a: 'Francis', heures: 20, jalon: 'Packaging prêt', critere: 'Terrain' },
          { tache: 'Plan communication complet', r: 'Pierre-Luc', a: 'Francis', heures: 15, jalon: '100 % actions', critere: 'Réalisées' }
        ]
      },
      {
        id: 'INTERNE_FORMATION',
        nom: 'Axe 5 — Interne & formation',
        tasks: [
          { tache: 'Formation interne PRISME rodée', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 20, jalon: 'Formation maîtrisée', critere: 'Autonomie' },
          { tache: '+30 documents internes', r: 'Pierre-Luc', a: 'Francis', heures: 25, jalon: '+30 docs', critere: 'Utilisés' },
          { tache: 'Gestion documentaire PRISME', r: 'Jean-Michel', a: 'Francis', heures: 20, jalon: 'Fonctionnelle', critere: 'Docs gérés' }
        ]
      }
    ],
    totalHeures: 505,
    synthese: ['PRISME prouve sa valeur', 'Qualité et adoption élevées']
  },
  {
    trimestre: 'Q4 2026',
    theme: 'Pérennité & projection 2027',
    axes: [
      {
        id: 'PRISME_CLIENT_MONTEE',
        nom: 'Axe 1 — PRISME Client & objectifs annuels',
        tasks: [
          { tache: '120 → 150 clients PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 70, jalon: '150 clients actifs', critere: 'Clients + comités actifs' },
          { tache: '+150 documents livrés', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 55, jalon: '150 docs', critere: 'Utilisés' },
          { tache: 'Adoption cadre BZ (50 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 35, jalon: '50 % clients', critere: 'Cadre actif' }
        ]
      },
      {
        id: 'IA_BOT_SPIDER',
        nom: 'Axe 2 — IA, BOT & SPIDER',
        tasks: [
          { tache: 'Adoption BOT interne (75 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 30, jalon: '75 % employés', critere: 'Usage mesuré' },
          { tache: 'SPIDER complété (100 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 25, jalon: 'Skill complet', critere: 'Utilisation réelle' },
          { tache: 'Qualité BOT > 95 %', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 20, jalon: '95 % utiles', critere: 'Mesure qualité' }
        ]
      },
      {
        id: 'QUALITE_CERTIFICATION',
        nom: 'Axe 3 — Qualité & certification',
        tasks: [
          { tache: 'Plan certification PRISME', r: 'Pierre-Luc / Jean-Michel', a: 'Jean-Pierre', heures: 30, jalon: 'Plan prêt', critere: 'Critères définis' },
          { tache: 'Qualité PRISME 90 %', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 25, jalon: 'Audit OK', critere: 'Conforme' },
          { tache: 'Adoption PRISME Employé (75 %)', r: 'Gestionnaires', a: 'Jean-Pierre', heures: 25, jalon: '75 % employés', critere: 'Usage réel' }
        ]
      },
      {
        id: 'GOUVERNANCE_STRATEGIE',
        nom: 'Axe 4 — Gouvernance & stratégie',
        tasks: [
          { tache: 'Bilan roadmap 2026', r: 'Éric', a: 'Dany', heures: 20, jalon: 'Bilan produit', critere: 'Écarts documentés' },
          { tache: 'Roadmap PRISME 2027', r: 'Éric', a: 'Dany', heures: 30, jalon: 'Validée', critere: 'Go comité' },
          { tache: 'Gouvernance pérennité', r: 'Éric', a: 'Dany', heures: 15, jalon: 'Stable', critere: 'Aucun point bloquant' }
        ]
      },
      {
        id: 'VENTE_IMAGE',
        nom: 'Axe 5 — Vente & image',
        tasks: [
          { tache: 'Objectifs vente atteints', r: 'Pierre-Luc', a: 'Francis', heures: 25, jalon: 'Objectifs OK', critere: 'Résultats mesurés' },
          { tache: 'Image PRISME haut de gamme', r: 'Pierre-Luc', a: 'Francis', heures: 15, jalon: 'Image consolidée', critere: 'Feedback marché' },
          { tache: 'Onboarding vente 100 %', r: 'Pierre-Luc', a: 'Francis', heures: 10, jalon: 'Équipe prête', critere: 'Autonomie ventes' }
        ]
      }
    ],
    totalHeures: 465,
    synthese: ['PRISME pérenne et crédible', 'Aucune dette 2026', 'Projection 2027 prête']
  }
];

export default function GouvernancePRIsMEModal({ isOpen, onClose }: GouvernancePRIsMEModalProps) {
  const [activeTab, setActiveTab] = useState<'esprit' | 'architecture' | 'cadre' | 'maturite' | 'raci' | 'roadmap' | 'rituels'>('esprit');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['gouvernance_prisme_last_ai_analysis'] ?? null) : null;

  const tabs = [
    { id: 'esprit' as const, label: 'Esprit & Objectifs macro', icon: Eye },
    { id: 'architecture' as const, label: 'Architecture & gouvernance', icon: Shield },
    { id: 'cadre' as const, label: 'Cadre holistique (coaching)', icon: BookOpen },
    { id: 'maturite' as const, label: 'Maturité (Spider + Bar)', icon: Target },
    { id: 'raci' as const, label: 'Rôles & responsabilités', icon: Users },
    { id: 'roadmap' as const, label: 'Roadmap 2026', icon: Calendar },
    { id: 'rituels' as const, label: 'Gouvernance & rituels', icon: Shield },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-indigo-500/30 shadow-2xl shadow-indigo-500/20 flex flex-col">
              {/* Header */}
              <div className="relative p-3 border-b border-indigo-500/30 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                        Gouvernance PRIsME
                      </h2>
                      <p className="text-gray-400 text-xs">
                        Cadre holistique de maturité organisationnelle
                      </p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/30 border border-purple-400/50 text-xs text-purple-200" title="Disponible dans AI Assistant">
                          <Brain className="w-3.5 h-3.5" />
                          AI
                        </span>
                        {lastAnalysisDate && (
                          <span className="text-gray-400 text-xs flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            Dernière synchro LEX: {formatLastAnalysisDate(lastAnalysisDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Avatars participants clés */}
                    <div className="flex items-center gap-2">
                      <img src="/DB.png" alt="Dany Bourassa" className="w-8 h-8 rounded-full border-2 border-indigo-400" title="Dany Bourassa - PRIsME Master" />
                      <img src="/EH.png" alt="Éric Hains" className="w-8 h-8 rounded-full border-2 border-indigo-400" title="Éric Hains - Owner PRIsME" />
                      <img src="/FM.png" alt="Francis Marquis" className="w-8 h-8 rounded-full border-2 border-indigo-400" title="Francis Marquis - Facilitateur" />
                      <img src="/JMC.png" alt="Jean-Michel Caouette" className="w-8 h-8 rounded-full border-2 border-indigo-400" title="Jean-Michel Caouette - Innovation/Exécution" />
                      <img src="/JP.png" alt="Jean-Pierre Leclerc" className="w-8 h-8 rounded-full border-2 border-indigo-400" title="Jean-Pierre Leclerc - Qualité/KPI + PRIsME Employé" />
                      <img src="/PLF.png" alt="Pierre-Luc Fortin" className="w-8 h-8 rounded-full border-2 border-indigo-400" title="Pierre-Luc Fortin - Communication & Commercialisation" />
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors ml-2"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Tabs Navigation */}
              <div className="border-b border-indigo-500/30 bg-slate-900/50 px-6 pt-3 pb-0 overflow-x-auto">
                <div className="flex gap-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold text-sm transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-b from-indigo-600 to-purple-600 text-white shadow-lg'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <AnimatePresence mode="wait">
                  {activeTab === 'esprit' && <EspritTab />}
                  {activeTab === 'architecture' && <ArchitectureGouvernanceTab />}
                  {activeTab === 'cadre' && <CadreTab />}
                  {activeTab === 'maturite' && <MaturiteTab />}
                  {activeTab === 'raci' && <RACITab />}
                  {activeTab === 'roadmap' && <RoadmapTab />}
                  {activeTab === 'rituels' && <RituelsTab />}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// ONGLET 1 — ESPRIT & OBJECTIFS MACRO
// ============================================================================
function EspritTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Esprit & Objectifs macro
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Vision BZ — pourquoi elle existe, ce qu&apos;elle transforme, ce qu&apos;elle ne doit jamais devenir
        </p>
      </div>

      {/* Vision PRIsME */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Eye className="w-6 h-6 text-indigo-400" />
          Vision BZ
        </h4>
        <p className="text-gray-300 leading-relaxed mb-3 text-lg font-semibold">
          Votre partenaire technologique et stratégique pour structurer la performance et assurer la résilience de vos affaires.
        </p>
      </div>

      {/* Ce que PRIsME va améliorer */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-indigo-400" />
          Ce que PRIsME va améliorer
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          PRIsME existe pour répondre à un enjeu fondamental :<br />
          <strong className="text-white">livrer la même excellence, partout, tout le temps, indépendamment des individus</strong>.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Concrètement, PRIsME permet de :
        </p>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Standardiser l'excellence</strong>
              <p className="text-sm text-gray-400 mt-1">Chaque Prime livre une qualité constante, peu importe son niveau d'expérience.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Transformer l'expertise en avantage concurrentiel pour BZ</strong>
              <p className="text-sm text-gray-400 mt-1">Le savoir de BZ n'est plus implicite ou individuel : il devient structuré, démontrable et différenciant.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Réduire les silos d'information</strong>
              <p className="text-sm text-gray-400 mt-1">L'information critique est centralisée, contextualisée et accessible au bon moment.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Accélérer l'onboarding et l'autonomie</strong>
              <p className="text-sm text-gray-400 mt-1">Les nouveaux employés comprennent rapidement leur rôle, les attentes et les processus.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Mesurer, piloter et améliorer</strong>
              <p className="text-sm text-gray-400 mt-1">Les KPI deviennent des outils de décision, pas de simples rapports.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Ce que PRIsME règle concrètement chez BZ */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-emerald-400" />
          Ce que PRIsME règle concrètement chez BZ
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Avant PRIsME, plusieurs enjeux limitaient notre capacité à scaler l'excellence :
        </p>
        <ul className="space-y-4 text-gray-300">
          <li className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Incohérence de livraison</strong>
              <p className="text-sm text-gray-400 mt-1">Chaque Prime livrait selon son style et son expérience.</p>
              <p className="text-sm text-emerald-400 mt-1">→ PRIsME rend la qualité reproductible et prévisible.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Silos d'information</strong>
              <p className="text-sm text-gray-400 mt-1">L'information était dispersée entre les personnes et les outils.</p>
              <p className="text-sm text-emerald-400 mt-1">→ PRIsME centralise et structure la connaissance.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Onboarding lent et dépendant des individus</strong>
              <p className="text-sm text-gray-400 mt-1">L'intégration reposait trop sur l'accompagnement humain.</p>
              <p className="text-sm text-emerald-400 mt-1">→ PRIsME Employé accélère et sécurise l'autonomie.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Manque de visibilité et de mesure</strong>
              <p className="text-sm text-gray-400 mt-1">Difficile de démontrer la valeur livrée.</p>
              <p className="text-sm text-emerald-400 mt-1">→ PRIsME introduit des KPI clairs et suivis.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Différenciation floue sur le marché</strong>
              <p className="text-sm text-gray-400 mt-1">BZ était perçue comme un fournisseur TI parmi d'autres.</p>
              <p className="text-sm text-emerald-400 mt-1">→ PRIsME devient un différenciateur stratégique fort.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Ce que PRIsME ne doit JAMAIS devenir */}
      <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-6 h-6 text-red-400" />
          Ce que PRIsME ne doit JAMAIS devenir
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Pour rester un levier de performance, PRIsME ne doit pas dériver.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4 font-semibold">
          PRIsME ne doit pas être :
        </p>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Un simple outil de documentation</strong>
              <p className="text-sm text-gray-400 mt-1">Ce n'est pas un wiki, mais un cadre vivant.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Une bureaucratie lourde</strong>
              <p className="text-sm text-gray-400 mt-1">PRIsME doit simplifier la prise de décision, pas la ralentir.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Un substitut à l&apos;humain</strong>
              <p className="text-sm text-gray-400 mt-1">PRIsME et l&apos;IA soutiennent le jugement humain, ils ne doivent jamais le remplacer.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Réservé aux experts</strong>
              <p className="text-sm text-gray-400 mt-1">Il doit être compréhensible et utilisable par toutes les parties prenantes.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Statique</strong>
              <p className="text-sm text-gray-400 mt-1">PRIsME doit évoluer avec BZ, ses clients et les technologies.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Passage du Niveau 1 → Niveau 2 */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-indigo-400" />
          Passage du Niveau 1 → Niveau 2
        </h4>
        <p className="text-gray-300 leading-relaxed mb-3">
          Le passage du <strong className="text-white">Niveau 1 (POC, expérimentation)</strong> au <strong className="text-white">Niveau 2 (adoption structurée)</strong> est une étape charnière.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Ce passage signifie que PRIsME cesse d'être une initiative prometteuse pour devenir un <strong className="text-white">cadre opérationnel officiel</strong>.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4 font-semibold">
          Pour réussir cette transition, les éléments suivants doivent être en place :
        </p>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Gouvernance formelle</strong>
              <p className="text-sm text-gray-400 mt-1">Comité PRIsME actif, rôles clairs et assumés.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Processus standardisés</strong>
              <p className="text-sm text-gray-400 mt-1">Livraison PRIsME Client reproductible et maîtrisée.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Formation structurée</strong>
              <p className="text-sm text-gray-400 mt-1">Parcours clair pour les nouveaux Primes.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">KPI de base</strong>
              <p className="text-sm text-gray-400 mt-1">Mesure de l'adoption, de la qualité et de la valeur livrée.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Communication cohérente et régulière</strong>
              <p className="text-sm text-gray-400 mt-1">Vision partagée avec les leaders et les équipes.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Objectifs macro 2026 */}
      <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl p-6 border-2 border-indigo-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-indigo-400" />
          Objectifs macro 2026
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          À l'horizon 2026, PRIsME doit être un cadre mature, mesuré et reconnu, tant à l'interne qu'à l'externe.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4 font-semibold">
          Nos objectifs macro sont :
        </p>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Atteindre un niveau de maturité 4</strong> sur les axes critiques : PRIsME Client, Livraison, Technologie + gestion de projets intégrée.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Intégrer la gestion de projets directement dans PRIsME</strong> (inspirée PMBOK, sans jargon) pour gouvernance projet, livrables structurés, visibilité risques/avancement/valeur.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Déployer PRIsME Employé pour tous les rôles</strong> entièrement intégré à l'onboarding et au quotidien.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Rendre le BOT PRIsME pleinement opérationnel</strong> pour automatiser les tâches répétitives et guider les Primes.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Disposer d'un catalogue d'actifs complet</strong> avec une interface de recherche performante.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Démontrer un ROI mesurable</strong> via des KPI documentés et suivis.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Positionner PRIsME comme différenciateur clé</strong> avec des arguments de vente clairs et des cas clients concrets.
            </div>
          </li>
        </ul>
      </div>

      {/* Phrase clé à retenir */}
      <div className="bg-gradient-to-r from-purple-600/20 via-indigo-600/20 to-purple-600/20 rounded-xl p-6 border-2 border-purple-500/30">
        <div className="text-center">
          <h4 className="text-lg font-bold text-white mb-3 flex items-center justify-center gap-2">
            <Target className="w-5 h-5 text-purple-400" />
            Phrase clé à retenir
          </h4>
          <p className="text-xl font-semibold text-white leading-relaxed italic">
            PRIsME n'existe pas pour faire plus.<br />
            Il existe pour faire mieux, de façon reproductible et mesurable.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET — ARCHITECTURE & GOUVERNANCE (clone PRIsMEOverviewModal – onglet Gouvernance)
// ============================================================================
function ArchitectureGouvernanceTab() {
  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h3 className="text-2xl font-bold text-white mb-2">Gouvernance & Conformité PRIsME</h3>
        <p className="text-gray-300 text-sm">Standards de gouvernance pour maintenir l&apos;excellence PRIsME</p>
      </div>

      <div className="grid gap-6">
        <div className="bg-white/5 p-6 rounded-xl border border-blue-300/60">
          <h4 className="text-xl font-semibold text-blue-100 mb-4">📋 Processus de Révision</h4>
          <ul className="space-y-3 text-blue-100 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-blue-300 font-bold">Q1:</span>
              <div>
                <strong>Révision Trimestrielle:</strong> Le Comité d&apos;Excellence BZ se réunit pour revoir les standards
                de chaque département et s&apos;assurer de leur pertinence.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-300 font-bold">Q2:</span>
              <div>
                <strong>Mise à Jour des Visions:</strong> Les 3 experts (Dany, Dany, François) ajoutent de nouvelles
                visions basées sur les apprentissages récents.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-300 font-bold">Q3:</span>
              <div>
                <strong>Feedback Employés:</strong> Intégration des suggestions d&apos;amélioration des employés
                qui utilisent PRIsME au quotidien.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-300 font-bold">Q4:</span>
              <div>
                <strong>Bilan Annuel:</strong> Présentation des évolutions PRIsME au Dany CA/CE et planification
                stratégique pour l&apos;année suivante.
              </div>
            </li>
          </ul>
        </div>

        <div className="bg-white/5 p-6 rounded-xl border border-emerald-300/60">
          <h4 className="text-xl font-semibold text-emerald-100 mb-4">👥 Rôles & Responsabilités</h4>
          <div className="space-y-4 text-sm text-emerald-50">
            <div className="bg-emerald-900/40 p-4 rounded-lg border border-emerald-300/60">
              <h5 className="font-bold text-emerald-100 mb-2">🌐 PRIsME Master → Prismes Employés</h5>
              <p>
                Chaque employé possède son prisme personnel alimenté par PRIsME Master BZ + TactiCE (stratégie/tactique) + l’outil RH (dossier employé).
                Les mises à jour Master se propagent automatiquement vers les prismes employés.
              </p>
            </div>
            <div className="bg-emerald-900/40 p-4 rounded-lg border border-emerald-300/60">
              <h5 className="font-bold text-emerald-100 mb-2">🧭 Gouvernance Prisme Employé</h5>
              <p>
                Responsables de ligne / gestionnaires : propriétaires de leur équipe (création, revue, activation des accès).
                CE valide les standards, Dany garde le pouvoir de veto/alignement final.
              </p>
            </div>
            <div className="bg-emerald-900/40 p-4 rounded-lg border border-emerald-300/60">
              <h5 className="font-bold text-emerald-100 mb-2">🤝 Gouvernance Prisme Client</h5>
              <p>
                Le Prime est responsable du prisme de son client (création, personnalisation, mise à jour).
                Le CE vérifie la conformité, Dany valide les cas sensibles.
              </p>
            </div>
            <div className="bg-emerald-900/40 p-4 rounded-lg border border-emerald-300/60">
              <h5 className="font-bold text-emerald-100 mb-2">🧡 Dany Bourassa - Président</h5>
              <p>
                Responsable de la vision globale PRIsME. Rédaction des visions holistiques et authentiques.
                Validation finale de tous les contenus.
              </p>
            </div>
            <div className="bg-emerald-900/40 p-4 rounded-lg border border-emerald-300/60">
              <h5 className="font-bold text-emerald-100 mb-2">❤️ Dany - CA/CE</h5>
              <p>
                Focus sur l&apos;angle développement d&apos;affaires et commercial. Visions orientées croissance,
                clients et différenciation marché.
              </p>
            </div>
            <div className="bg-emerald-900/40 p-4 rounded-lg border border-emerald-300/60">
              <h5 className="font-bold text-emerald-100 mb-2">💙 François - VP Techno</h5>
              <p>
                Angle technique et innovation. Visions sur l&apos;architecture, les outils, l&apos;automatisation
                et l&apos;excellence technologique.
              </p>
            </div>
            <div className="bg-emerald-900/40 p-4 rounded-lg border border-emerald-300/60">
              <h5 className="font-bold text-emerald-100 mb-2">👨‍💼 Gestionnaires de Département</h5>
              <p>
                Responsables de proposer des améliorations pour leur département respectif et de s&apos;assurer
                que leur équipe utilise PRIsME comme référence.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 p-6 rounded-xl border border-amber-300/60">
          <h4 className="text-xl font-semibold text-amber-100 mb-4">📌 RACI — Gouvernance PRIsME (employés &amp; clients)</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-amber-50">
              <thead>
                <tr className="bg-amber-900/60 text-left">
                  <th className="px-3 py-2">Tâche</th>
                  <th className="px-3 py-2">Dany</th>
                  <th className="px-3 py-2">CE</th>
                  <th className="px-3 py-2">Gestionnaires</th>
                  <th className="px-3 py-2">Prime (client)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-800/60">
                <tr>
                  <td className="px-3 py-2 font-semibold">Créer prisme employé</td>
                  <td className="px-3 py-2">I</td>
                  <td className="px-3 py-2">C</td>
                  <td className="px-3 py-2">R/A</td>
                  <td className="px-3 py-2">-</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold">Mettre à jour contenu (Master → employé)</td>
                  <td className="px-3 py-2">A</td>
                  <td className="px-3 py-2">R</td>
                  <td className="px-3 py-2">C</td>
                  <td className="px-3 py-2">-</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold">Alimentation TactiCE / RH vers prisme employé</td>
                  <td className="px-3 py-2">I</td>
                  <td className="px-3 py-2">C</td>
                  <td className="px-3 py-2">R/A</td>
                  <td className="px-3 py-2">-</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold">Créer / maintenir prisme client</td>
                  <td className="px-3 py-2">I</td>
                  <td className="px-3 py-2">C</td>
                  <td className="px-3 py-2">C</td>
                  <td className="px-3 py-2">R/A</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold">Conformité / qualité prismes (employé &amp; client)</td>
                  <td className="px-3 py-2">A</td>
                  <td className="px-3 py-2">R</td>
                  <td className="px-3 py-2">C</td>
                  <td className="px-3 py-2">I</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-amber-200 mt-3">
            R = Responsible, A = Accountable, C = Consulted, I = Informed
          </p>
        </div>

        <div className="bg-white/5 p-6 rounded-xl border border-purple-300/60">
          <h4 className="text-xl font-semibold text-purple-100 mb-4">📊 Métriques de Succès</h4>
          <ul className="space-y-3 text-purple-100 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-purple-300 font-bold">📈</span>
              <div>
                <strong>Adoption:</strong> 100% des nouveaux employés doivent explorer PRIsME dans leur première semaine.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-300 font-bold">💬</span>
              <div>
                <strong>Engagement:</strong> Minimum 3 feedbacks par département par trimestre.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-300 font-bold">⭐</span>
              <div>
                <strong>Qualité:</strong> Score de satisfaction employé &gt; 85% sur l&apos;utilité de PRIsME.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-300 font-bold">🎯</span>
              <div>
                <strong>Impact:</strong> Mesure de l&apos;amélioration des standards BZ année après année.
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 2 — CADRE HOLISTIQUE PRIsME (COACHING)
// ============================================================================
function CadreTab() {
  const [expandedAxe, setExpandedAxe] = useState<number | null>(null);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Cadre holistique PRIsME – Axes de maturité (0–5)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Les 10 axes PRIsME avec coaching détaillé pour chaque niveau de maturité (0-5, style ITIL)
        </p>
      </div>

      <div className="space-y-4">
        {axesPRIsME.map((axe) => (
          <div key={axe.id} className="bg-white/5 rounded-xl border border-indigo-500/20 overflow-hidden">
            <button
              onClick={() => setExpandedAxe(expandedAxe === axe.id ? null : axe.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                  {axe.id}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white">{axe.nom}</h4>
                  {axe.avatars && axe.avatars.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {axe.avatars.map((avatarName, idx) => {
                        const avatarSrc = avatarMap[avatarName];
                        return avatarSrc ? (
                          avatarSrc === '$' ? (
                            <div 
                              key={idx}
                              className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 border border-indigo-400 flex items-center justify-center text-white font-bold text-[10px] shadow-sm"
                              title={avatarName}
                            >
                              $
                            </div>
                          ) : avatarSrc === '👤' ? (
                            <div 
                              key={idx}
                              className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 border border-indigo-400 flex items-center justify-center text-white text-xs shadow-sm"
                              title={avatarName}
                            >
                              👤
                            </div>
                          ) : (
                            <img 
                              key={idx}
                              src={avatarSrc} 
                              alt={avatarName}
                              className="w-5 h-5 rounded-full border border-indigo-400 object-contain bg-white/10"
                              title={avatarName}
                            />
                          )
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className={`transform transition-transform ${expandedAxe === axe.id ? 'rotate-180' : ''}`}>
                <X className="w-5 h-5 text-gray-400" />
              </div>
            </button>

            {expandedAxe === axe.id && (
              <div className="p-6 border-t border-indigo-500/20 space-y-4">
                {/* Intention */}
                <div>
                  <h5 className="text-sm font-bold text-indigo-400 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Intention
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.intention}</p>
                </div>

                {/* Pourquoi critique */}
                <div>
                  <h5 className="text-sm font-bold text-yellow-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Pourquoi c'est critique
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.pourquoiCritique}</p>
                </div>

                {/* Concrètement BZ */}
                <div>
                  <h5 className="text-sm font-bold text-emerald-400 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Concrètement chez BZ
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.concretementBZ}</p>
                </div>

                {/* Erreurs fréquentes */}
                <div>
                  <h5 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Erreurs fréquentes
                  </h5>
                  <ul className="space-y-2">
                    {axe.erreursFrequentes.map((erreur, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                        <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <span>{erreur}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Niveaux de maturité */}
                <div>
                  <h5 className="text-sm font-bold text-purple-400 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Niveaux 0 → 5
                  </h5>
                  <div className="space-y-2">
                    {axe.niveaux.map((n) => (
                      <div key={n.niveau} className="bg-slate-800/50 rounded-lg p-3 border border-indigo-500/20">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-indigo-400">{n.niveau}</span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs font-semibold text-purple-300">{n.label}</span>
                        </div>
                        <p className="text-gray-300 text-xs leading-relaxed">{n.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* À faire maintenant */}
                {axe.aFaireMaintenant && axe.aFaireMaintenant.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-blue-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      À faire maintenant (N+1)
                    </h5>
                    <ul className="space-y-2">
                      {axe.aFaireMaintenant.map((action, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preuves visibles */}
                {axe.preuvesVisibles && axe.preuvesVisibles.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-emerald-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Preuves visibles
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {axe.preuvesVisibles.map((preuve, idx) => (
                        <span key={idx} className="px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded border border-emerald-500/30">
                          {preuve}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Phrase de clôture */}
      <div className="bg-gradient-to-r from-purple-600/20 via-indigo-600/20 to-purple-600/20 rounded-xl p-6 border-2 border-purple-500/30">
        <div className="text-center">
          <p className="text-lg font-semibold text-white leading-relaxed italic">
            Chaque axe PRIsME est une responsabilité collective,<br />
            mais une redevabilité claire.<br />
            La maturité ne s'improvise pas, elle se gouverne.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 3 — VUE MATURITÉ
// ============================================================================
function MaturiteTab() {
  // Convertir pour le graphique radar (échelle 0-5 vers 0-100)
  const chartData = maturiteData.map(item => ({
    category: item.axe.length > 15 ? item.axe.substring(0, 15) + '...' : item.axe,
    Actuel: (item.actuel / 5) * 100,
    Cible: (item.cible / 5) * 100,
  }));

  const actuelAverage = (maturiteData.reduce((sum, item) => sum + item.actuel, 0) / maturiteData.length).toFixed(1);
  const cibleAverage = (maturiteData.reduce((sum, item) => sum + item.cible, 0) / maturiteData.length).toFixed(1);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Maturité (Spider + Bar)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Évaluation de la maturité actuelle et cible pour fin 2026 — Niveaux 0-5 (style ITIL)
        </p>
      </div>

      {/* Scores moyens */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
          <div className="text-sm text-yellow-300 font-medium">Score Actuel (AS-IS)</div>
          <div className="text-2xl font-bold text-yellow-200">{actuelAverage}/5</div>
          <div className="text-xs text-yellow-400">État actuel</div>
        </div>
        <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
          <div className="text-sm text-emerald-300 font-medium">Score Cible (TARGET)</div>
          <div className="text-2xl font-bold text-emerald-200">{cibleAverage}/5</div>
          <div className="text-xs text-emerald-400">Objectif fin 2026</div>
        </div>
      </div>

      {/* Graphique Spider */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Graphique Radar - Maturité PRIsME</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
              <PolarGrid stroke="#4b5563" />
              <PolarAngleAxis 
                dataKey="category" 
                tick={{ fontSize: 11, fill: '#d1d5db' }}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickCount={6}
              />
              
              <Radar
                name="Actuel (AS-IS)"
                dataKey="Actuel"
                stroke="#fbbf24"
                fill="#fbbf24"
                fillOpacity={0.2}
                strokeWidth={3}
                dot={{ fill: '#fbbf24', strokeWidth: 2, r: 4 }}
              />
              
              <Radar
                name="Cible (TARGET)"
                dataKey="Cible"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.1}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
              />
              
              <Legend 
                wrapperStyle={{ paddingTop: '20px', color: '#d1d5db' }}
                iconType="circle"
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Bar Chart - Détail par axe</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={maturiteData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
              <XAxis 
                dataKey="axe" 
                angle={-45} 
                textAnchor="end" 
                height={120}
                tick={{ fontSize: 10, fill: '#d1d5db' }}
              />
              <YAxis 
                domain={[0, 5]}
                tick={{ fontSize: 10, fill: '#d1d5db' }}
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #4b5563', borderRadius: '8px' }}
                labelStyle={{ color: '#d1d5db' }}
              />
              <Legend />
              <Bar dataKey="actuel" fill="#fbbf24" name="Actuel (AS-IS)" />
              <Bar dataKey="cible" fill="#10b981" name="Cible (TARGET)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Échelle */}
      <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
        <h4 className="text-lg font-bold text-white mb-3">Échelle de maturité</h4>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">0 =</span>
            <span>Inexistant</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">1 =</span>
            <span>POC / Expérimentation</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">2 =</span>
            <span>Adoption structurée</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">3 =</span>
            <span>Maturité opérationnelle</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">4 =</span>
            <span>Excellence</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">5 =</span>
            <span>Référence marché</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 4 — RÔLES & RESPONSABILITÉS (RACI)
// ============================================================================
function RACITab() {
  const raciColors = {
    R: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
    A: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300',
    C: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300',
    I: 'bg-purple-500/20 border-purple-500/50 text-purple-300'
  };

  const raciLabels = {
    R: 'Responsable (fait le travail)',
    A: 'Accountable (décide et répond)',
    C: 'Consulté (donne son avis)',
    I: 'Informé (est tenu au courant)'
  };

  // Extraire tous les rôles uniques et les trier (personnes avant groupes)
  const allRoles = new Set<string>();
  raciData.forEach(activity => {
    Object.keys(activity.roles).forEach(role => allRoles.add(role));
  });
  
  const groupes = ['Comité PRIsME', 'Directeurs de compte', 'Gestionnaire', 'Primes'];
  const personnes = Array.from(allRoles).filter(role => !groupes.includes(role));
  const groupesPresents = Array.from(allRoles).filter(role => groupes.includes(role));
  
  // Trier les personnes : Dany (1er), Éric (2e), puis les autres par ordre alphabétique
  const personnesTriees = personnes.sort();
  const personnesOrdonnees: string[] = [];
  
  // Dany en 1er
  if (personnesTriees.includes('Dany Bourassa')) {
    personnesOrdonnees.push('Dany Bourassa');
  }
  
  // Éric en 2e
  if (personnesTriees.includes('Éric Hains')) {
    personnesOrdonnees.push('Éric Hains');
  }
  
  // Les autres personnes triées alphabétiquement
  const autresPersonnes = personnesTriees.filter(p => p !== 'Dany Bourassa' && p !== 'Éric Hains');
  personnesOrdonnees.push(...autresPersonnes.sort());
  
  // Ajouter les groupes (triés)
  const uniqueRoles = [...personnesOrdonnees, ...groupesPresents.sort()];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Rôles & responsabilités (RACI)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Matrice RACI pour la gouvernance PRIsME
        </p>
      </div>

      {/* Légende RACI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(raciLabels).map(([key, label]) => (
          <div key={key} className={`${raciColors[key as keyof typeof raciColors]} rounded-lg p-3 border-2 text-center`}>
            <div className="font-bold text-lg mb-1">{key}</div>
            <div className="text-xs">{label}</div>
          </div>
        ))}
      </div>

      {/* Tableau RACI */}
      <div className="bg-white/5 rounded-xl border border-indigo-500/20 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-indigo-500/30">
              <th className="text-left p-4 text-white font-bold">Activité</th>
              {uniqueRoles.map(role => (
                <th key={role} className="text-center p-4 text-white font-bold min-w-[120px]">
                  <div className="flex flex-col items-center gap-2">
                    {avatarMap[role] ? (
                      avatarMap[role] === '$' ? (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 border-2 border-indigo-400 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                          $
                        </div>
                      ) : avatarMap[role] === '👤' ? (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 border-2 border-indigo-400 flex items-center justify-center text-white text-lg shadow-lg">
                          👤
                        </div>
                      ) : (
                        <img 
                          src={avatarMap[role]} 
                          alt={role}
                          className="w-8 h-8 rounded-full border-2 border-indigo-400 object-contain bg-white/10"
                        />
                      )
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 border-2 border-indigo-400 flex items-center justify-center text-xs text-white">
                        {role.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                    )}
                    <span className="text-xs">{role}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {raciData.map((activity, idx) => (
              <tr key={idx} className="border-b border-indigo-500/20 hover:bg-white/5">
                <td className="p-4 text-gray-300 font-medium">{activity.activite}</td>
                {uniqueRoles.map(role => {
                  const raciValue = (activity.roles as unknown as Record<string, string>)[role] || '';
                  return (
                    <td key={role} className="text-center p-4">
                      {raciValue && (
                        <span className={`inline-block px-3 py-1 rounded-full font-bold text-sm border-2 ${raciColors[raciValue as keyof typeof raciColors]}`}>
                          {raciValue}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Rôles spécifiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/LogoPrismeSmall.png" alt="Comité" className="w-8 h-8 rounded-full border-2 border-indigo-400 object-contain bg-white/10" />
            <h4 className="text-lg font-bold text-white">Rôle du Comité PRIsME</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            Le Comité PRIsME se réunit 2 fois par mois. Dany participe 1 fois par mois. Les décisions sont prises collectivement, avec Francis comme facilitateur des réunions.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/EH.png" alt="Éric Hains" className="w-8 h-8 rounded-full border-2 border-indigo-400" />
            <h4 className="text-lg font-bold text-white">Rôle de l'Owner</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            L'Owner (<strong className="text-white">Éric Hains</strong>) est responsable de l'exécution opérationnelle, de la qualité des livraisons PRIsME Client et de l'adoption par les Primes.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/DB.png" alt="Dany Bourassa" className="w-8 h-8 rounded-full border-2 border-indigo-400" />
            <h4 className="text-lg font-bold text-white">Rôle du PRIsME Master</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            Le PRIsME Master (<strong className="text-white">Dany Bourassa</strong>) définit la vision stratégique, valide les décisions majeures et garantit l'alignement. Effort décroissant : 20h/sem → 10h en mars → 5h en juin.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/JP.png" alt="Jean-Pierre Leclerc" className="w-8 h-8 rounded-full border-2 border-indigo-400" />
            <h4 className="text-lg font-bold text-white">Jean-Pierre Leclerc</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            <strong className="text-white">Focus : Qualité/KPI + PRIsME Employé</strong>. <span className="text-red-300">Ne pas impliquer dans PRIsME Client</span>. Responsable de la mesure, du suivi qualité et du déploiement PRIsME Employé.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/FM.png" alt="Francis Marquis" className="w-8 h-8 rounded-full border-2 border-indigo-400" />
            <h4 className="text-lg font-bold text-white">Francis Marquis</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            <strong className="text-white">Facilitateur des réunions</strong> + gouvernance. Anime les 2 rencontres mensuelles du Comité PRIsME.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/PLF.png" alt="Pierre-Luc Fortin" className="w-8 h-8 rounded-full border-2 border-indigo-400" />
            <h4 className="text-lg font-bold text-white">Pierre-Luc Fortin</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            <strong className="text-white">Communication interne + Commercialisation</strong> (stratégie PULL) + gestion documentaire PRIsME.
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <img src="/JMC.png" alt="Jean-Michel Caouette" className="w-8 h-8 rounded-full border-2 border-indigo-400" />
            <h4 className="text-lg font-bold text-white">Jean-Michel Caouette</h4>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            <strong className="text-white">Innovation/Exécution + Documentation</strong>. Porte les initiatives d'innovation et la documentation PRIsME.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 5 — ROADMAP ANNUELLE 2026
// ============================================================================
function RoadmapTab() {
  const [roadmapMode, setRoadmapMode] = useState<'execution' | 'strategie'>('execution');
  const [isEditingPMO, setIsEditingPMO] = useState(false);
  const [selectedExecutionPerson, setSelectedExecutionPerson] = useState<string | null>(null);
  const [pmoProgress, setPmoProgress] = useState<Record<string, number>>({});
  const [pmoQuarterOverrides, setPmoQuarterOverrides] = useState<
    Record<string, { theme?: string; totalHeures?: number; synthese?: string[] }>
  >({});
  const [pmoTaskOverrides, setPmoTaskOverrides] = useState<
    Record<string, Partial<RoadmapOperationnelTask>>
  >({});
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [pmoHistory, setPmoHistory] = useState<
    Array<{
      ts: string;
      user?: string;
      kind: 'task_progress' | 'task_meta' | 'quarter_meta';
      quarter: string;
      axeId?: string;
      taskIdx?: number;
      label: string;
      from: string;
      to: string;
    }>
  >([]);

  const lastSavedRef = useRef<{
    progress: Record<string, number>;
    quarters: Record<string, { theme?: string; totalHeures?: number; synthese?: string[] }>;
    tasks: Record<string, Partial<RoadmapOperationnelTask>>;
  }>({ progress: {}, quarters: {}, tasks: {} });

  const getTaskKey = (quarterTrimestre: string, axeId: string, idx: number) => `${quarterTrimestre}::${axeId}::${idx}`;

  // Charger l'état PMO depuis la DB (partagé)
  useEffect(() => {
    (async () => {
      try {
        const state = await getRoadmapPmoState();
        const progress = state.progress || {};
        const taskOverrides = state.tasks || {};
        
        // Initialiser TACTICE intégré (Axe 2) à 50% si pas déjà défini
        const tacticeKey = 'Q1 2026::CONTENU_METHODE::0';
        if (!(tacticeKey in progress)) {
          progress[tacticeKey] = 50;
        }
        // Initialiser le critère de validation pour TACTICE intégré
        if (!(tacticeKey in taskOverrides) || !taskOverrides[tacticeKey].critere) {
          taskOverrides[tacticeKey] = {
            ...taskOverrides[tacticeKey],
            critere: 'Onglet Projet créé et 90% fonctionnel'
          };
        }
        
        // Initialiser PRISME Employé V1 (Axe 2) à 100% si pas déjà défini
        const prismeEmployeKey = 'Q1 2026::CONTENU_METHODE::2';
        if (!(prismeEmployeKey in progress)) {
          progress[prismeEmployeKey] = 100;
        }
        
        // Initialiser PRISME Département V1 (Axe 2) à 100% si pas déjà défini
        const prismeDepartementKey = 'Q1 2026::CONTENU_METHODE::3';
        if (!(prismeDepartementKey in progress)) {
          progress[prismeDepartementKey] = 100;
        }
        
        // Initialiser l'avancement pour Axe 1 — PRISME Client & livraison (Q1 2026) si pas déjà défini
        const axe1ClientsKey = 'Q1 2026::PRISME_CLIENT_LIVRAISON::0'; // 15 → 50 clients PRISME
        if (!(axe1ClientsKey in progress)) {
          progress[axe1ClientsKey] = 34;
        }
        const axe1R2Key = 'Q1 2026::PRISME_CLIENT_LIVRAISON::1'; // R2 / documents (20 clients)
        if (!(axe1R2Key in progress)) {
          progress[axe1R2Key] = 10;
        }
        const axe1RodageKey = 'Q1 2026::PRISME_CLIENT_LIVRAISON::2'; // Rodage PRISME client
        if (!(axe1RodageKey in progress)) {
          progress[axe1RodageKey] = 40;
        }
        const axe1CatalogueKey = 'Q1 2026::PRISME_CLIENT_LIVRAISON::3'; // Catalogue d'actifs PRISME
        if (!(axe1CatalogueKey in progress)) {
          progress[axe1CatalogueKey] = 100;
        }

        // Initialiser l'avancement pour Axe 3 — Vente & communication (Q1 2026) si pas déjà défini
        const axe3PlanCommKey = 'Q1 2026::VENTE_COMMUNICATION::2'; // Plan communication PRISME
        if (!(axe3PlanCommKey in progress)) {
          progress[axe3PlanCommKey] = 20;
        }
        
        // Initialiser Gestion droits & accès à 100% si pas déjà défini
        const gestionDroitsKey = 'Q2 2026::PRISME_EMPLOYE_METHODE::2';
        if (!(gestionDroitsKey in progress)) {
          progress[gestionDroitsKey] = 100;
        }
        
        // Initialiser BOT PRISME V1 à 50% si pas déjà défini
        const botPrismeKey = 'Q2 2026::IA_BOT::0';
        if (!(botPrismeKey in progress)) {
          progress[botPrismeKey] = 50;
        }
        
        // Initialiser Suivi projet IA à 75% si pas déjà défini
        const suiviProjetIAKey = 'Q2 2026::IA_BOT::1';
        if (!(suiviProjetIAKey in progress)) {
          progress[suiviProjetIAKey] = 75;
        }
        
        setPmoProgress(progress);
        setPmoQuarterOverrides(state.quarters || {});
        setPmoTaskOverrides(taskOverrides);
        setPmoHistory(state.history || []);
        lastSavedRef.current = {
          progress: progress,
          quarters: state.quarters || {},
          tasks: taskOverrides,
        };
      } catch (e) {
        console.error('Erreur chargement roadmap PMO (DB):', e);
      }
    })();
  }, []);

  const savePMOState = async () => {
    try {
      const state = await saveRoadmapPmoState({
        progress: pmoProgress,
        quarters: pmoQuarterOverrides,
        tasks: pmoTaskOverrides,
      });
      setPmoProgress(state.progress || {});
      setPmoQuarterOverrides(state.quarters || {});
      setPmoTaskOverrides(state.tasks || {});
      setPmoHistory(state.history || []);
      lastSavedRef.current = {
        progress: state.progress || {},
        quarters: state.quarters || {},
        tasks: state.tasks || {},
      };
    } catch (e) {
      console.error('Erreur sauvegarde roadmap PMO (DB):', e);
    }
  };

  const clampPct = (v: unknown) => {
    const n = typeof v === 'number' ? v : Number(v || 0);
    return Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));
  };

  const getTaskPct = (quarterTrimestre: string, axeId: string, idx: number) =>
    clampPct(pmoProgress[getTaskKey(quarterTrimestre, axeId, idx)] ?? 0);

  const getExecutionPersonStatus = (personPct: number, global: number) => {
    if (!Number.isFinite(personPct) || !Number.isFinite(global)) return 'neutral' as const;
    // Sur cible ou en avance: vert
    if (personPct >= global - 5) return 'ok' as const;
    // Légèrement en retard: jaune
    if (personPct >= global - 20) return 'warning' as const;
    // En retard significatif vs rythme global: rouge
    return 'risk' as const;
  };

  const getExecutionTaskStatus = (taskPct: number, quarterPct: number) => {
    if (!Number.isFinite(taskPct) || !Number.isFinite(quarterPct)) return 'neutral' as const;
    if (taskPct >= quarterPct - 5) return 'ok' as const;
    if (taskPct >= quarterPct - 20) return 'warning' as const;
    return 'risk' as const;
  };

  const getQuarterWeight = (quarter: RoadmapOperationnelQuarter) => {
    // Pondération principale = total heures du trimestre (éditable). Sinon fallback = somme des heures des tâches.
    const overrideHours = pmoQuarterOverrides[quarter.trimestre]?.totalHeures;
    const base = typeof overrideHours === 'number' && Number.isFinite(overrideHours)
      ? overrideHours
      : typeof quarter.totalHeures === 'number' && Number.isFinite(quarter.totalHeures)
        ? quarter.totalHeures
        : undefined;
    if (typeof base === 'number') return Math.max(0, base);

    let sum = 0;
    quarter.axes.forEach((axe) => {
      axe.tasks.forEach((t) => {
        const h = typeof t.heures === 'number' && Number.isFinite(t.heures) ? t.heures : 0;
        sum += h > 0 ? h : 1;
      });
    });
    return sum;
  };

  const quarterStats = useMemo(() => {
    const stats: Record<string, { pct: number; weight: number }> = {};

    roadmapOperationnel2026.forEach((quarter) => {
      let total = 0;
      let weighted = 0;

      quarter.axes.forEach((axe) => {
        axe.tasks.forEach((t, idx) => {
          const h = typeof t.heures === 'number' && Number.isFinite(t.heures) ? t.heures : 0;
          const w = h > 0 ? h : 1;
          const pct = getTaskPct(quarter.trimestre, axe.id, idx);
          total += w;
          weighted += w * pct;
        });
      });

      const pct = total > 0 ? Math.round(weighted / total) : 0;
      const weight = getQuarterWeight(quarter);
      stats[quarter.trimestre] = { pct, weight };
    });

    return stats;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmoProgress, pmoQuarterOverrides]);

  const globalPct = useMemo(() => {
    // Global = moyenne pondérée des % de chaque trimestre (Q1-Q4)
    let total = 0;
    let weighted = 0;
    roadmapOperationnel2026.forEach((quarter) => {
      const s = quarterStats[quarter.trimestre] || { pct: 0, weight: 0 };
      const w = typeof s.weight === 'number' && Number.isFinite(s.weight) ? s.weight : 0;
      total += w;
      weighted += w * s.pct;
    });
    if (total <= 0) return 0;
    return Math.round(weighted / total);
  }, [quarterStats]);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Roadmap 2026
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Deux vues complémentaires : exécution (PMO) et stratégie (capabilités)
        </p>
      </div>

      {/* Sélecteur de vue */}
      <div className="flex items-center justify-center">
        <div className="inline-flex rounded-xl border border-indigo-500/30 bg-slate-900/40 p-1">
          <button
            type="button"
            onClick={() => setRoadmapMode('execution')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition ${
              roadmapMode === 'execution'
                ? 'bg-indigo-500/30 text-white border border-indigo-500/50'
                : 'text-indigo-200 hover:text-white'
            }`}
          >
            Exécution (PMO)
          </button>
          <button
            type="button"
            onClick={() => setRoadmapMode('strategie')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition ${
              roadmapMode === 'strategie'
                ? 'bg-indigo-500/30 text-white border border-indigo-500/50'
                : 'text-indigo-200 hover:text-white'
            }`}
          >
            Stratégie (boussole)
          </button>
        </div>
      </div>

      {roadmapMode === 'execution' && (
        <div className="space-y-6">
          <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="text-xl font-bold text-white mb-2">Roadmap opérationnel 2026 (équipe)</h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Document de pilotage opérationnel (PMO) : <strong className="text-white">qui fait quoi</strong>, <strong className="text-white">quand</strong>,
                  et <strong className="text-white">comment on valide</strong>. Conçu pour avancer vite et mesurer l’avancement par jalons.
                </p>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-gray-300">
                    <span className="font-semibold">Avancement global</span>
                    <span className="font-bold text-indigo-200">{globalPct}%</span>
                  </div>
                  <div className="mt-2 h-2.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                      style={{ width: `${globalPct}%` }}
                    />
                  </div>
                </div>

          {/* Résumé effort par personne (exécution PMO) */}
          {(() => {
            const effortParPersonne: Record<string, number> = {};
            const progressParPersonne: Record<string, { total: number; progress: number }> = {};

            // Mapping entre axes opérationnels et activités du RACI
            const axeIdToRaci: Record<string, string> = {
              PRISME_CLIENT_LIVRAISON: 'Livraison PRIsME Client',
              PRISME_CLIENT_MONTEE: 'Livraison PRIsME Client',
              CONTENU_METHODE: 'PRIsME Employé et Département',
              PRISME_EMPLOYE_METHODE: 'PRIsME Employé et Département',
              ADOPTION_FORMATION: 'Formation & Adoption',
              ADOPTION_EXPERIENCE_CLIENT: 'Formation & Adoption',
              INTERNE_FORMATION: 'Formation & Adoption',
              GOUVERNANCE_QUALITE: 'Qualité, KPI & Suivi',
              GOUVERNANCE_SUIVI: 'Qualité, KPI & Suivi',
              QUALITE_CERTIFICATION: 'Qualité, KPI & Suivi',
              IA_BOT: 'Technologie & Outils (BOT)',
              IA_BOT_SPIDER: 'Technologie & Outils (BOT)',
              VENTE_COMMUNICATION: 'Commercialisation PRIsME',
              VENTE_POSITIONNEMENT: 'Commercialisation PRIsME',
              VENTE_IMAGE: 'Commercialisation PRIsME',
              GOUVERNANCE_STRATEGIE: 'Gouvernance & Décisions',
            };

            roadmapOperationnel2026.forEach((quarter) => {
              quarter.axes.forEach((axe) => {
                const nomRACI = axeIdToRaci[axe.id];
                if (!nomRACI) return;
                const activite = raciData.find((a) => a.activite === nomRACI);
                if (!activite) return;

                const personnesAR: string[] = [];
                Object.entries(activite.roles).forEach(([nom, role]) => {
                  if (
                    (role === 'A' || role === 'R') &&
                    !['Comité PRIsME', 'Primes', 'Directeurs de compte', 'Gestionnaire'].includes(nom)
                  ) {
                    personnesAR.push(nom);
                  }
                });

                const rolesPourCalcul: Record<string, string> = {};
                Object.entries(activite.roles).forEach(([nom, role]) => {
                  if (role && typeof role === 'string') {
                    rolesPourCalcul[nom] = role;
                  }
                });

                const personnesA = personnesAR.filter((nom) => rolesPourCalcul[nom] === 'A');
                const personnesR = personnesAR.filter((nom) => rolesPourCalcul[nom] === 'R');

                axe.tasks.forEach((t, idx) => {
                  const heures = typeof t.heures === 'number' && Number.isFinite(t.heures) ? t.heures : 0;
                  const pct = getTaskPct(quarter.trimestre, axe.id, idx);

                  if (heures > 0 && personnesAR.length > 0) {
                    const effortA = personnesA.length > 0 ? (heures * 0.6) / personnesA.length : 0;
                    const effortR = personnesR.length > 0 ? (heures * 0.4) / personnesR.length : 0;

                    personnesA.forEach((nom) => {
                      effortParPersonne[nom] = (effortParPersonne[nom] || 0) + effortA;
                      if (!progressParPersonne[nom]) {
                        progressParPersonne[nom] = { total: 0, progress: 0 };
                      }
                      progressParPersonne[nom].total += effortA;
                      progressParPersonne[nom].progress += effortA * (pct / 100);
                    });

                    personnesR.forEach((nom) => {
                      effortParPersonne[nom] = (effortParPersonne[nom] || 0) + effortR;
                      if (!progressParPersonne[nom]) {
                        progressParPersonne[nom] = { total: 0, progress: 0 };
                      }
                      progressParPersonne[nom].total += effortR;
                      progressParPersonne[nom].progress += effortR * (pct / 100);
                    });
                  }
                });
              });
            });

            const progressFinalParPersonne: Record<string, number> = {};
            Object.entries(progressParPersonne).forEach(([nom, data]) => {
              if (data.total > 0) {
                progressFinalParPersonne[nom] = Math.round((data.progress / data.total) * 100);
              } else {
                progressFinalParPersonne[nom] = 0;
              }
            });

            const personnesTriees = Object.entries(effortParPersonne)
              .sort((a, b) => b[1] - a[1])
              .filter(([_, effort]) => effort > 0);

            if (personnesTriees.length === 0) return null;

            return (
              <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-6 border-2 border-indigo-500/30">
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Target className="w-6 h-6 text-indigo-400" />
                  Répartition effort 2026 par personne (exécution)
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {personnesTriees.map(([nom, effort]) => {
                    const avatarSrc = avatarMap[nom];
                    const progressPersonne = progressFinalParPersonne[nom] || 0;
                    const status = getExecutionPersonStatus(progressPersonne, globalPct);
                    const cardClass =
                      status === 'ok'
                        ? 'bg-emerald-900/40 border-emerald-500/40'
                        : status === 'warning'
                          ? 'bg-amber-900/40 border-amber-500/40'
                          : status === 'risk'
                            ? 'bg-rose-900/40 border-rose-500/40'
                            : 'bg-slate-900/50 border-indigo-500/20';

                    return (
                      <button
                        type="button"
                        key={nom}
                        onClick={() => setSelectedExecutionPerson(nom)}
                        className={`${cardClass} rounded-lg p-3 text-left w-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-400`}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          {avatarSrc ? (
                            avatarSrc === '$' || avatarSrc === '👤' ? (
                              <div
                                className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center text-xs font-bold text-indigo-300 cursor-help"
                                title={nom}
                              >
                                {avatarSrc}
                              </div>
                            ) : (
                              <img
                                src={avatarSrc}
                                alt={nom}
                                title={nom}
                                className="w-8 h-8 rounded-full border-2 border-indigo-500/50 object-cover cursor-help"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                }}
                              />
                            )
                          ) : (
                            <div
                              className="w-8 h-8 rounded-full bg-indigo-500/20 border-2 border-indigo-500/50 flex items-center justify-center text-xs text-white cursor-help"
                              title={nom}
                            >
                              {nom
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .slice(0, 2)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-200 truncate">{nom.split(' ')[0]}</p>
                            <p className="text-xs text-gray-400 truncate">{nom.split(' ').slice(1).join(' ')}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                            <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 24 24">
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                fill="none"
                                stroke="rgba(16, 185, 129, 0.2)"
                                strokeWidth="2"
                              />
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                fill="none"
                                stroke="rgba(16, 185, 129, 0.9)"
                                strokeWidth="2.5"
                                strokeDasharray={`${2 * Math.PI * 10 * (progressPersonne / 100)} ${2 * Math.PI * 10}`}
                                strokeLinecap="round"
                              />
                            </svg>
                            <span className="absolute text-xs font-bold text-emerald-100">{progressPersonne}%</span>
                          </div>
                          <div className="text-[0.7rem] text-gray-300 text-center leading-tight">
                            <div>{Math.round(effort)} h prévues</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Détail par personne (modal interne) */}
          {selectedExecutionPerson && (() => {
            type ExecutionPersonTask = {
              quarter: string;
              axeId: RoadmapOperationnelAxeId;
              axeNom: string;
              tache: string;
              heures: number;
              pct: number;
              quarterPct: number;
              role: 'A' | 'R';
            };

            const axeIdToRaci: Record<string, string> = {
              PRISME_CLIENT_LIVRAISON: 'Livraison PRIsME Client',
              PRISME_CLIENT_MONTEE: 'Livraison PRIsME Client',
              CONTENU_METHODE: 'PRIsME Employé et Département',
              PRISME_EMPLOYE_METHODE: 'PRIsME Employé et Département',
              ADOPTION_FORMATION: 'Formation & Adoption',
              ADOPTION_EXPERIENCE_CLIENT: 'Formation & Adoption',
              INTERNE_FORMATION: 'Formation & Adoption',
              GOUVERNANCE_QUALITE: 'Qualité, KPI & Suivi',
              GOUVERNANCE_SUIVI: 'Qualité, KPI & Suivi',
              QUALITE_CERTIFICATION: 'Qualité, KPI & Suivi',
              IA_BOT: 'Technologie & Outils (BOT)',
              IA_BOT_SPIDER: 'Technologie & Outils (BOT)',
              VENTE_COMMUNICATION: 'Commercialisation PRIsME',
              VENTE_POSITIONNEMENT: 'Commercialisation PRIsME',
              VENTE_IMAGE: 'Commercialisation PRIsME',
              GOUVERNANCE_STRATEGIE: 'Gouvernance & Décisions',
            };

            const tasks: ExecutionPersonTask[] = [];

            roadmapOperationnel2026.forEach((quarter) => {
              const quarterPct = quarterStats[quarter.trimestre]?.pct ?? 0;

              quarter.axes.forEach((axe) => {
                const nomRACI = axeIdToRaci[axe.id];
                if (!nomRACI) return;
                const activite = raciData.find((a) => a.activite === nomRACI);
                if (!activite) return;

                const baseRole = activite.roles[
                  selectedExecutionPerson as keyof typeof activite.roles
                ] as 'A' | 'R' | 'C' | 'I' | undefined;

                let role: 'A' | 'R' | undefined;
                if (baseRole === 'A' || baseRole === 'R') {
                  role = baseRole;
                }

                if (!role) return;
                const roleFinal: 'A' | 'R' = role;

                axe.tasks.forEach((t, idx) => {
                  const pct = getTaskPct(quarter.trimestre, axe.id, idx);
                  tasks.push({
                    quarter: quarter.trimestre,
                    axeId: axe.id,
                    axeNom: axe.nom,
                    tache: t.tache,
                    heures: t.heures,
                    pct,
                    quarterPct,
                    role: roleFinal,
                  });
                });
              });
            });

            if (tasks.length === 0) return null;

            const tasksByQuarterAndAxe: Record<
              string,
              Record<string, ExecutionPersonTask[]>
            > = {};

            tasks.forEach((t) => {
              if (!tasksByQuarterAndAxe[t.quarter]) {
                tasksByQuarterAndAxe[t.quarter] = {};
              }
              if (!tasksByQuarterAndAxe[t.quarter][t.axeNom]) {
                tasksByQuarterAndAxe[t.quarter][t.axeNom] = [];
              }
              tasksByQuarterAndAxe[t.quarter][t.axeNom].push(t);
            });

            const avatarSrc = avatarMap[selectedExecutionPerson];

            return (
              <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70">
                <div className="bg-slate-950/95 rounded-2xl border border-indigo-500/40 shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-indigo-500/30">
                    <div className="flex items-center gap-3">
                      {avatarSrc ? (
                        <img
                          src={avatarSrc}
                          alt={selectedExecutionPerson}
                          className="w-9 h-9 rounded-full border-2 border-indigo-400 object-cover"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-indigo-500/30 border-2 border-indigo-400 flex items-center justify-center text-xs text-white">
                          {selectedExecutionPerson
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .slice(0, 2)}
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-bold text-white">
                          Feuille de route — {selectedExecutionPerson}
                        </div>
                        <div className="text-xs text-gray-400">
                          Vue personnelle par trimestre et par axe (Exécution PRIsME 2026)
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedExecutionPerson(null)}
                      className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    >
                      Fermer
                    </button>
                  </div>
                  <div className="px-6 py-4 text-xs text-gray-400 border-b border-indigo-500/20">
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-emerald-500/80" />{' '}
                        <span>Vert = sur le rythme ou en avance</span>
                      </span>
                      <span className="inline-flex items-center gap-1 ml-4">
                        <span className="inline-block w-3 h-3 rounded-full bg-amber-500/80" />{' '}
                        <span>Jaune = léger retard (à surveiller)</span>
                      </span>
                      <span className="inline-flex items-center gap-1 ml-4">
                        <span className="inline-block w-3 h-3 rounded-full bg-rose-500/80" />{' '}
                        <span>Rouge = en retard (prioritaire)</span>
                      </span>
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    {Object.entries(tasksByQuarterAndAxe).map(([quarter, axes]) => (
                      <div key={quarter} className="bg-slate-900/60 rounded-xl border border-indigo-500/25 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-indigo-300" />
                            <div className="text-sm font-bold text-white">{quarter}</div>
                          </div>
                          <div className="text-[0.7rem] text-gray-400">
                            Avancement trimestre:{' '}
                            <span className="font-semibold text-indigo-200">
                              {quarterStats[quarter]?.pct ?? 0}%
                            </span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {Object.entries(axes).map(([axeNom, axeTasks]) => (
                            <div key={axeNom} className="bg-slate-950/60 rounded-lg border border-indigo-500/15 p-3">
                              <div className="text-xs font-bold text-indigo-200 mb-2">{axeNom}</div>
                              <div className="space-y-1.5">
                                {axeTasks.map((t, idx) => {
                                  const status = getExecutionTaskStatus(t.pct, t.quarterPct);
                                  const rowClass =
                                    status === 'ok'
                                      ? 'border-emerald-500/40 bg-emerald-900/20'
                                      : status === 'warning'
                                        ? 'border-amber-500/40 bg-amber-900/20'
                                        : status === 'risk'
                                          ? 'border-rose-500/40 bg-rose-900/25'
                                          : 'border-slate-600/40 bg-slate-900/40';
                                  return (
                                    <div
                                      key={`${axeNom}-${idx}`}
                                      className={`${rowClass} rounded-md px-3 py-2 flex items-center justify-between gap-3 border`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-white truncate">
                                          {t.tache}
                                        </div>
                                        <div className="text-[0.7rem] text-gray-300 flex flex-wrap gap-3 mt-1">
                                          <span>
                                            Rôle:{' '}
                                            <span className="font-semibold text-indigo-200">
                                              {t.role === 'A' ? 'A (Accountable)' : 'R (Responsible)'}
                                            </span>
                                          </span>
                                          <span>
                                            Estimé:{' '}
                                            <span className="font-semibold text-gray-100">
                                              {t.heures} h
                                            </span>
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <div className="text-[0.7rem] text-gray-300">
                                            Avancement tâche
                                          </div>
                                          <div className="text-sm font-bold text-emerald-100">
                                            {t.pct}%
                                          </div>
                                        </div>
                                        <div className="w-20 h-2 rounded-full bg-slate-800 overflow-hidden">
                                          <div
                                            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500"
                                            style={{ width: `${t.pct}%` }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsHistoryOpen(true)}
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-bold transition"
                >
                  Historique
                </button>
                {!isEditingPMO ? (
                  <button
                    type="button"
                    onClick={() => setIsEditingPMO(true)}
                    className="px-4 py-2 rounded-lg bg-indigo-500/25 hover:bg-indigo-500/35 border border-indigo-500/40 text-white text-sm font-bold transition"
                  >
                    Modifier
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        savePMOState();
                        setIsEditingPMO(false);
                      }}
                      className="px-4 py-2 rounded-lg bg-emerald-500/25 hover:bg-emerald-500/35 border border-emerald-500/40 text-white text-sm font-bold transition"
                    >
                      Sauvegarder
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingPMO(false)}
                      className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-bold transition"
                      title="Annuler (garde la dernière sauvegarde)"
                    >
                      Fermer
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Historique PMO */}
          {isHistoryOpen && (
            <div className="bg-slate-950/40 rounded-xl border border-indigo-500/25 p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <div className="text-lg font-bold text-white">Historique des modifications</div>
                  <div className="text-xs text-gray-400">
                    Logs enregistrés lors de “Sauvegarder”.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsHistoryOpen(false)}
                    className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-xs font-bold transition"
                  >
                    Fermer
                  </button>
                </div>
              </div>

              {pmoHistory.length === 0 ? (
                <div className="text-sm text-gray-400 py-6 text-center">Aucun log pour le moment.</div>
              ) : (
                <div className="max-h-[320px] overflow-y-auto divide-y divide-white/5">
                  {pmoHistory.map((h, idx) => (
                    <div key={`${h.ts}-${idx}`} className="py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-indigo-200 truncate">{h.label}</div>
                          <div className="text-xs text-gray-400">
                        {new Date(h.ts).toLocaleString('fr-CA')} • {h.user || '—'} •{' '}
                        {h.kind === 'task_progress'
                          ? 'Avancement tâche'
                          : h.kind === 'task_meta'
                          ? 'Données tâche'
                          : 'Info trimestre'}
                          </div>
                        </div>
                        <div className="text-xs font-bold text-white whitespace-nowrap">
                          <span className="text-gray-300">{h.from}</span>
                          <span className="text-gray-500 mx-2">→</span>
                          <span className="text-emerald-200">{h.to}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-6">
            {roadmapOperationnel2026.map((quarter) => (
              <div key={quarter.trimestre} className="bg-slate-900/30 rounded-xl border border-indigo-500/20 p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-white flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-indigo-300" />
                      {quarter.trimestre} — {pmoQuarterOverrides[quarter.trimestre]?.theme ?? quarter.theme}
                    </h4>
                    {(pmoQuarterOverrides[quarter.trimestre]?.synthese ?? quarter.synthese) &&
                      (pmoQuarterOverrides[quarter.trimestre]?.synthese ?? quarter.synthese)!.length > 0 && (
                      <ul className="mt-2 text-sm text-gray-300 list-disc pl-6 space-y-1">
                        {(pmoQuarterOverrides[quarter.trimestre]?.synthese ?? quarter.synthese)!.map((s, idx) => (
                          <li key={idx}>{s}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {typeof (pmoQuarterOverrides[quarter.trimestre]?.totalHeures ?? quarter.totalHeures) === 'number' && (
                      <div className="bg-indigo-500/15 border border-indigo-500/30 rounded-lg px-4 py-2 text-center">
                        <div className="text-xs text-gray-400">Total estimé</div>
                        <div className="text-lg font-bold text-indigo-200">
                          ~{pmoQuarterOverrides[quarter.trimestre]?.totalHeures ?? quarter.totalHeures} h
                        </div>
                      </div>
                    )}
                    <div className="bg-white/10 border border-indigo-500/25 rounded-lg px-4 py-2 min-w-[120px]">
                      <div className="flex items-center justify-between text-xs text-gray-300">
                        <span className="font-semibold">Avancement</span>
                        <span className="font-bold text-indigo-200">{quarterStats[quarter.trimestre]?.pct ?? 0}%</span>
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                          style={{ width: `${quarterStats[quarter.trimestre]?.pct ?? 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {isEditingPMO && (
                  <div className="mb-5 bg-white/5 rounded-lg border border-indigo-500/20 p-4">
                    <div className="text-sm font-bold text-indigo-200 mb-3">Modifier infos générales</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-indigo-300 mb-1">Thème</label>
                        <input
                          value={pmoQuarterOverrides[quarter.trimestre]?.theme ?? quarter.theme}
                          onChange={(e) =>
                            setPmoQuarterOverrides((prev) => ({
                              ...prev,
                              [quarter.trimestre]: { ...(prev[quarter.trimestre] || {}), theme: e.target.value },
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-indigo-500/30 text-white text-sm focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-indigo-300 mb-1">Total estimé (heures)</label>
                        <input
                          type="number"
                          min={0}
                          value={pmoQuarterOverrides[quarter.trimestre]?.totalHeures ?? quarter.totalHeures ?? 0}
                          onChange={(e) =>
                            setPmoQuarterOverrides((prev) => ({
                              ...prev,
                              [quarter.trimestre]: {
                                ...(prev[quarter.trimestre] || {}),
                                totalHeures: Number(e.target.value || 0),
                              },
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-indigo-500/30 text-white text-sm focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-xs font-bold text-indigo-300 mb-1">Synthèse (une par ligne)</label>
                        <textarea
                          rows={2}
                          value={(pmoQuarterOverrides[quarter.trimestre]?.synthese ?? quarter.synthese ?? []).join('\n')}
                          onChange={(e) =>
                            setPmoQuarterOverrides((prev) => ({
                              ...prev,
                              [quarter.trimestre]: {
                                ...(prev[quarter.trimestre] || {}),
                                synthese: e.target.value
                                  .split('\n')
                                  .map((s) => s.trim())
                                  .filter(Boolean),
                              },
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-indigo-500/30 text-white text-sm focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {quarter.axes.map((axe) => (
                    <div key={axe.id} className="bg-slate-950/30 rounded-lg border border-indigo-500/10 p-4">
                      <h5 className="text-lg font-bold text-indigo-200 mb-3">{axe.nom}</h5>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-indigo-500/20">
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300">Tâche</th>
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300 w-48">R</th>
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300 w-32">A</th>
                              <th className="text-right py-2 px-3 text-xs font-bold text-indigo-300 w-24">Heures</th>
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300 w-48">Jalon</th>
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300 w-64">Critère de validation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {axe.tasks.map((t, idx) => (
                              <tr key={idx} className="border-b border-indigo-500/10 hover:bg-white/5 align-top">
                                {(() => {
                                  const k = getTaskKey(quarter.trimestre, axe.id, idx);
                                  const ov = pmoTaskOverrides[k] || {};
                                  const merged = { ...t, ...ov };
                                  return (
                                    <>
                                      <td className="py-2 px-3 text-sm text-gray-200">
                                        {isEditingPMO ? (
                                          <textarea
                                            rows={2}
                                            value={merged.tache}
                                            onChange={(e) =>
                                              setPmoTaskOverrides((prev) => ({
                                                ...prev,
                                                [k]: { ...(prev[k] || {}), tache: e.target.value },
                                              }))
                                            }
                                            className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                          />
                                        ) : (
                                          merged.tache
                                        )}
                                      </td>
                                      <td className="py-2 px-3 text-sm text-gray-300">
                                        {isEditingPMO ? (
                                          <input
                                            value={merged.r}
                                            onChange={(e) =>
                                              setPmoTaskOverrides((prev) => ({
                                                ...prev,
                                                [k]: { ...(prev[k] || {}), r: e.target.value },
                                              }))
                                            }
                                            className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                          />
                                        ) : (
                                          merged.r
                                        )}
                                      </td>
                                      <td className="py-2 px-3 text-sm text-gray-300">
                                        {isEditingPMO ? (
                                          <input
                                            value={merged.a}
                                            onChange={(e) =>
                                              setPmoTaskOverrides((prev) => ({
                                                ...prev,
                                                [k]: { ...(prev[k] || {}), a: e.target.value },
                                              }))
                                            }
                                            className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                          />
                                        ) : (
                                          merged.a
                                        )}
                                      </td>
                                      <td className="py-2 px-3 text-right text-sm font-semibold text-indigo-200">
                                        {isEditingPMO ? (
                                          <input
                                            type="number"
                                            min={0}
                                            value={Number(merged.heures || 0)}
                                            onChange={(e) => {
                                              const v = Number(e.target.value || 0);
                                              setPmoTaskOverrides((prev) => ({
                                                ...prev,
                                                [k]: { ...(prev[k] || {}), heures: Number.isFinite(v) ? v : 0 },
                                              }));
                                            }}
                                            className="w-20 text-right px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                          />
                                        ) : (
                                          `${merged.heures}h`
                                        )}
                                      </td>
                                    </>
                                  );
                                })()}
                                <td className="py-2 px-3 text-sm text-gray-300">
                                  <div className="flex items-center justify-between gap-2">
                                    {(() => {
                                      const k = getTaskKey(quarter.trimestre, axe.id, idx);
                                      const ov = pmoTaskOverrides[k] || {};
                                      const jalon = ov.jalon ?? t.jalon;
                                      return isEditingPMO ? (
                                        <input
                                          value={jalon}
                                          onChange={(e) =>
                                            setPmoTaskOverrides((prev) => ({
                                              ...prev,
                                              [k]: { ...(prev[k] || {}), jalon: e.target.value },
                                            }))
                                          }
                                          className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                        />
                                      ) : (
                                        <div className="font-medium">{jalon}</div>
                                      );
                                    })()}
                                    <div className="text-xs font-bold text-indigo-200">
                                      {Math.max(0, Math.min(100, pmoProgress[getTaskKey(quarter.trimestre, axe.id, idx)] ?? 0))}%
                                    </div>
                                  </div>
                                  <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                                      style={{
                                        width: `${Math.max(
                                          0,
                                          Math.min(100, pmoProgress[getTaskKey(quarter.trimestre, axe.id, idx)] ?? 0)
                                        )}%`,
                                      }}
                                    />
                                  </div>
                                  {isEditingPMO && (
                                    <div className="mt-2 flex items-center gap-2">
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={pmoProgress[getTaskKey(quarter.trimestre, axe.id, idx)] ?? 0}
                                        onChange={(e) => {
                                          const v = Math.max(0, Math.min(100, Number(e.target.value || 0)));
                                          setPmoProgress((prev) => ({
                                            ...prev,
                                            [getTaskKey(quarter.trimestre, axe.id, idx)]: v,
                                          }));
                                        }}
                                        className="w-20 px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-gray-400">%</span>
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-sm text-gray-300">
                                  {(() => {
                                    const k = getTaskKey(quarter.trimestre, axe.id, idx);
                                    const ov = pmoTaskOverrides[k] || {};
                                    const critere = ov.critere ?? t.critere;
                                    return isEditingPMO ? (
                                      <textarea
                                        rows={2}
                                        value={critere}
                                        onChange={(e) =>
                                          setPmoTaskOverrides((prev) => ({
                                            ...prev,
                                            [k]: { ...(prev[k] || {}), critere: e.target.value },
                                          }))
                                        }
                                        className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                      />
                                    ) : (
                                      critere
                                    );
                                  })()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {roadmapMode === 'strategie' && (
        <div className="space-y-6">
          {/* Résumé effort par personne */}
          {(() => {
        // Fonction pour obtenir le % d'avancement d'un livrable
        const getLivrableProgress = (texte: string): number => {
          const livrablesCompletes100 = [
            'Déploiement PRIsME Employé pour 50% des employés',
            'Cloner TactiCE (Projet) dans PRIsME avec structure complète',
            'Gouvernance projet structurée (rôles, décisions, escalade) - pilotée par DB et EH',
            'Onboarding intégré à PRIsME Employé avec checklist par rôle',
            'RACI complet documenté et visible dans PRIsME'
          ];
          const livrablesCompletes75 = [
            'Gestion documentaire 8 1/2 x 11 impeccable (PDF export optimisé)',
            'Automatisation génération documents (rapports, livrables)',
            'Tous les rôles ont leur PRIsME Employé complet et à jour',
            'Processus livraison PRIsME Client optimisé (workflow, checkpoints)',
            'Documentation processus par rôle (responsabilités, livrables, outils)',
            'Lien avec Shield / Decision Desk opérationnel (firewall mental)',
            'Format Decision Packet standardisé pour escalade vers Dany',
            'Rituels gouvernance documentés (2 réunions/mois, présence Dany 1x)',
            'Processus décision formalisé (critères, escalade, délais)'
          ];
          const livrablesCompletes50 = [
            'Formation gestionnaires sur leur responsabilité contenu départemental',
            'Gestionnaires autonomes sur leur contenu départemental',
            'Mise à jour régulière PRIsME Employé (mensuelle minimum)',
            'Feedback boucle intégrée (suggestions, améliorations)',
            'Intégration PRIsME Employé avec outils quotidiens (SSO, notifications)',
            'Intégration projets TactiCE avec PRIsME Client (livraison traçable)',
            'BOT PRIsME opérationnel (réponses FAQ, guidage processus)',
            'Interface conversationnelle pour guider les Primes',
            'Sécurité intégrée (classification, accès, audit)',
            'Qualité 100% vérifiée avant livraison (checklist, validation)',
            'Templates livraison PRIsME Client standardisés (structure, contenu)',
            'Pages PRIsME Employé versionnées et mises à jour régulièrement',
            'Métriques adoption PRIsME Employé (visites, temps onboarding, autonomie)',
            'Règles d\'escalade claires (quand escalader, quand non)'
          ];
          const livrablesCompletes25 = [
            'Automatisation tâches répétitives (création pages, notifications, rappels)',
            'Amélioration continue processus (rétrospectives, ajustements)'
          ];
          if (livrablesCompletes100.includes(texte)) return 100;
          if (livrablesCompletes75.includes(texte)) return 75;
          if (livrablesCompletes50.includes(texte)) return 50;
          if (livrablesCompletes25.includes(texte)) return 25;
          return 0;
        };

        // Calculer le total d'heures par personne et le % d'avancement
        const effortParPersonne: Record<string, number> = {};
        const progressParPersonne: Record<string, { total: number; progress: number }> = {};
        
        roadmap2026.forEach(quarter => {
          quarter.axes.forEach(axe => {
            // Récupérer les personnes A et R pour cet axe
            const mapping: Record<string, string> = {
              'Livraison & Exécution': 'Livraison PRIsME Client',
              'Maturité globale': 'Qualité, KPI & Suivi'
            };
            const nomRACI = mapping[axe.axe] || axe.axe;
            const activite = raciData.find(a => a.activite === nomRACI);
            
            if (activite) {
              const personnesAR: string[] = [];
              Object.entries(activite.roles).forEach(([nom, role]) => {
                if ((role === 'A' || role === 'R') && !['Comité PRIsME', 'Primes', 'Directeurs de compte', 'Gestionnaire'].includes(nom)) {
                  personnesAR.push(nom);
                }
              });
              
              // Créer une copie des rôles pour les calculs
              const rolesPourCalcul: Record<string, string> = {};
              Object.entries(activite.roles).forEach(([nom, role]) => {
                if (role && typeof role === 'string') {
                  rolesPourCalcul[nom] = role;
                }
              });
              
              // Vérifier s'il y a déjà un A dans la liste (après avoir créé rolesPourCalcul)
              const hasA = personnesAR.some(nom => rolesPourCalcul[nom] === 'A');
              
              // Pour Livraison & Exécution, ajouter Dany seulement s'il n'y a pas déjà d'A
              if (nomRACI === 'Livraison PRIsME Client' && !hasA && !personnesAR.includes('Dany Bourassa')) {
                personnesAR.push('Dany Bourassa');
                rolesPourCalcul['Dany Bourassa'] = 'A';
              }
              
              // Répartir l'effort : A prend 60%, R partagent les 40% (divisé par nombre de R)
              const personnesA = personnesAR.filter(nom => rolesPourCalcul[nom] === 'A');
              const personnesR = personnesAR.filter(nom => rolesPourCalcul[nom] === 'R');
              
              axe.livrables.forEach((livrable: any) => {
                const texte = typeof livrable === 'string' ? livrable : livrable.texte;
                const effort = typeof livrable === 'string' ? 0 : livrable.effort || 0;
                const livrableProgress = getLivrableProgress(texte);
                
                if (effort > 0 && personnesAR.length > 0) {
                  // Répartition : A = 60% / nombre de A, R = 40% / nombre de R
                  const effortA = personnesA.length > 0 ? (effort * 0.6) / personnesA.length : 0;
                  const effortR = personnesR.length > 0 ? (effort * 0.4) / personnesR.length : 0;
                  
                  personnesA.forEach(nom => {
                    effortParPersonne[nom] = (effortParPersonne[nom] || 0) + effortA;
                    if (!progressParPersonne[nom]) {
                      progressParPersonne[nom] = { total: 0, progress: 0 };
                    }
                    progressParPersonne[nom].total += effortA;
                    progressParPersonne[nom].progress += effortA * (livrableProgress / 100);
                  });
                  personnesR.forEach(nom => {
                    effortParPersonne[nom] = (effortParPersonne[nom] || 0) + effortR;
                    if (!progressParPersonne[nom]) {
                      progressParPersonne[nom] = { total: 0, progress: 0 };
                    }
                    progressParPersonne[nom].total += effortR;
                    progressParPersonne[nom].progress += effortR * (livrableProgress / 100);
                  });
                }
              });
            }
          });
        });
        
        // Calculer le % d'avancement final pour chaque personne
        const progressFinalParPersonne: Record<string, number> = {};
        Object.entries(progressParPersonne).forEach(([nom, data]) => {
          if (data.total > 0) {
            progressFinalParPersonne[nom] = Math.round((data.progress / data.total) * 100);
          } else {
            progressFinalParPersonne[nom] = 0;
          }
        });
        
        const personnesTriees = Object.entries(effortParPersonne)
          .sort((a, b) => b[1] - a[1])
          .filter(([_, effort]) => effort > 0);
        
        return (
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-6 border-2 border-indigo-500/30 mb-6">
            <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Target className="w-6 h-6 text-indigo-400" />
              Répartition effort 2026 par personne
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {personnesTriees.map(([nom, effort]) => {
                const avatarSrc = avatarMap[nom];
                const progressPersonne = progressFinalParPersonne[nom] || 0;
                return (
                  <div key={nom} className="bg-slate-900/50 rounded-lg p-3 border border-indigo-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      {avatarSrc ? (
                        avatarSrc === '$' || avatarSrc === '👤' ? (
                          <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center text-xs font-bold text-indigo-300 cursor-help" title={nom}>
                            {avatarSrc}
                          </div>
                        ) : (
                          <img 
                            src={avatarSrc} 
                            alt={nom} 
                            title={nom}
                            className="w-8 h-8 rounded-full border-2 border-indigo-500/50 object-cover cursor-help"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        )
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-indigo-500/20 border-2 border-indigo-500/50 flex items-center justify-center text-xs text-white cursor-help" title={nom}>
                          {nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-300 truncate">{nom.split(' ')[0]}</p>
                        <p className="text-xs text-gray-400 truncate">{nom.split(' ').slice(1).join(' ')}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      {/* Cercle de progression */}
                      <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                        <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 24 24">
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            fill="none"
                            stroke="rgba(16, 185, 129, 0.2)"
                            strokeWidth="2"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            fill="none"
                            stroke="rgb(16, 185, 129)"
                            strokeWidth="2"
                            strokeDasharray={`${2 * Math.PI * 10 * (progressPersonne / 100)} ${2 * Math.PI * 10}`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-emerald-300">{progressPersonne}%</span>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold text-indigo-300">{Math.round(effort)}</p>
                        <p className="text-xs text-gray-400">heures</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-indigo-500/20">
              <p className="text-sm text-gray-400">
                <strong className="text-white">Total : {Math.round(Object.values(effortParPersonne).reduce((a, b) => a + b, 0))} heures</strong> réparties sur l'année 2026
              </p>
            </div>
          </div>
        );
          })()}

      <div className="space-y-6">
        {roadmap2026.map((quarter, qIdx) => {
          const colorMap = {
            'Q1 2026': 'from-indigo-500/20 to-indigo-600/20 border-indigo-500/30',
            'Q2 2026': 'from-purple-500/20 to-purple-600/20 border-purple-500/30',
            'Q3 2026': 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
            'Q4 2026': 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30'
          };
          const bgClass = colorMap[quarter.trimestre as keyof typeof colorMap];

          // Fonction pour calculer le % d'avancement d'un livrable
          const getLivrableProgress = (texte: string): number => {
            const livrablesCompletes100 = [
              'Déploiement PRIsME Employé pour 50% des employés',
              'Cloner TactiCE (Projet) dans PRIsME avec structure complète',
              'Gouvernance projet structurée (rôles, décisions, escalade) - pilotée par DB et EH',
              'Onboarding intégré à PRIsME Employé avec checklist par rôle',
              'RACI complet documenté et visible dans PRIsME'
            ];
            const livrablesCompletes75 = [
              'Gestion documentaire 8 1/2 x 11 impeccable (PDF export optimisé)',
              'Automatisation génération documents (rapports, livrables)',
              'Tous les rôles ont leur PRIsME Employé complet et à jour',
              'Processus livraison PRIsME Client optimisé (workflow, checkpoints)',
              'Documentation processus par rôle (responsabilités, livrables, outils)',
              'Lien avec Shield / Decision Desk opérationnel (firewall mental)',
              'Format Decision Packet standardisé pour escalade vers Dany',
              'Rituels gouvernance documentés (2 réunions/mois, présence Dany 1x)',
              'Processus décision formalisé (critères, escalade, délais)'
            ];
            const livrablesCompletes50 = [
              'Formation gestionnaires sur leur responsabilité contenu départemental',
              'Gestionnaires autonomes sur leur contenu départemental',
              'Mise à jour régulière PRIsME Employé (mensuelle minimum)',
              'Feedback boucle intégrée (suggestions, améliorations)',
              'Intégration PRIsME Employé avec outils quotidiens (SSO, notifications)',
              'Intégration projets TactiCE avec PRIsME Client (livraison traçable)',
              'BOT PRIsME opérationnel (réponses FAQ, guidage processus)',
              'Interface conversationnelle pour guider les Primes',
              'Sécurité intégrée (classification, accès, audit)',
              'Qualité 100% vérifiée avant livraison (checklist, validation)',
              'Templates livraison PRIsME Client standardisés (structure, contenu)',
              'Pages PRIsME Employé versionnées et mises à jour régulièrement',
              'Métriques adoption PRIsME Employé (visites, temps onboarding, autonomie)',
              'Règles d\'escalade claires (quand escalader, quand non)'
            ];
          const livrablesCompletes25 = [
            'Automatisation tâches répétitives (création pages, notifications, rappels)',
            'Amélioration continue processus (rétrospectives, ajustements)'
          ];
            if (livrablesCompletes100.includes(texte)) return 100;
            if (livrablesCompletes75.includes(texte)) return 75;
            if (livrablesCompletes50.includes(texte)) return 50;
            if (livrablesCompletes25.includes(texte)) return 25;
            return 0;
          };

          // Calculer le % d'avancement par axe
          const calculateAxeProgress = (axe: any): number => {
            if (!axe.livrables || axe.livrables.length === 0) return 0;
            let totalProgress = 0;
            let count = 0;
            axe.livrables.forEach((livrable: any) => {
              const texte = typeof livrable === 'string' ? livrable : livrable.texte;
              totalProgress += getLivrableProgress(texte);
              count++;
            });
            return count > 0 ? Math.round(totalProgress / count) : 0;
          };

          // Calculer le % d'avancement du trimestre (moyenne des axes)
          const calculateQuarterProgress = (): number => {
            if (!quarter.axes || quarter.axes.length === 0) return 0;
            let totalProgress = 0;
            let count = 0;
            quarter.axes.forEach((axe: any) => {
              const axeProgress = calculateAxeProgress(axe);
              totalProgress += axeProgress;
              count++;
            });
            return count > 0 ? Math.round(totalProgress / count) : 0;
          };

          const quarterProgress = calculateQuarterProgress();

          return (
            <div key={qIdx} className={`bg-gradient-to-br ${bgClass} rounded-xl p-6 border-2`}>
              <div className="mb-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                      <Calendar className="w-6 h-6" />
                      {quarter.trimestre}
                    </h4>
                    {quarter.intention && (
                      <p className="text-sm text-gray-300 italic ml-8">{quarter.intention}</p>
                    )}
                  </div>
                  {/* Cercle de progression pour le trimestre */}
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-xs text-gray-400 mb-1">Avancement</div>
                      <div className="text-lg font-bold text-white">{quarterProgress}%</div>
                    </div>
                    <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                      <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 24 24">
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          fill="none"
                          stroke="rgba(16, 185, 129, 0.2)"
                          strokeWidth="2"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          fill="none"
                          stroke="rgb(16, 185, 129)"
                          strokeWidth="2"
                          strokeDasharray={`${2 * Math.PI * 10 * (quarterProgress / 100)} ${2 * Math.PI * 10}`}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {quarter.axes.map((axe, aIdx) => {
                  // Récupérer les personnes A et R pour cet axe selon le RACI
                  const getPersonnesAR = (axeName: string) => {
                    // Mapping entre noms de la roadmap et noms du RACI
                    const mapping: Record<string, string> = {
                      'Livraison & Exécution': 'Livraison PRIsME Client', // Utiliser Livraison PRIsME Client comme référence
                      'Maturité globale': 'Qualité, KPI & Suivi' // Utiliser Qualité, KPI & Suivi comme référence
                    };
                    
                    const nomRACI = mapping[axeName] || axeName;
                    const activite = raciData.find(a => a.activite === nomRACI);
                    if (!activite) return [];
                    
                    const personnes: Array<{ nom: string; role: 'A' | 'R' | 'C' }> = [];
                    Object.entries(activite.roles).forEach(([nom, role]) => {
                      // Pour PRIsME Employé et Département, inclure aussi Dany (C)
                      if (nomRACI === 'PRIsME Employé et Département' && nom === 'Dany Bourassa') {
                        personnes.push({ nom, role: 'C' as 'A' | 'R' | 'C' });
                      } else if ((role === 'A' || role === 'R') && !['Comité PRIsME', 'Primes', 'Directeurs de compte', 'Gestionnaire'].includes(nom)) {
                        personnes.push({ nom, role: role as 'A' | 'R' | 'C' });
                      }
                    });
                    
                    // Vérifier s'il y a déjà un A dans la liste
                    const hasA = personnes.some(p => p.role === 'A');
                    
                    // Pour Livraison & Exécution, ajouter Dany Bourassa comme A seulement s'il n'y a pas déjà d'A
                    if (axeName === 'Livraison & Exécution' && !hasA && !personnes.find(p => p.nom === 'Dany Bourassa')) {
                      personnes.push({ nom: 'Dany Bourassa', role: 'A' });
                    }
                    return personnes;
                  };
                  
                  const personnesAR = getPersonnesAR(axe.axe);
                  
                  // Calculer le total d'heures pour cet axe
                  const totalEffortAxe = axe.livrables.reduce((total: number, livrable: any) => {
                    const effort = typeof livrable === 'string' ? 0 : livrable.effort || 0;
                    return total + effort;
                  }, 0);
                  
                  // Calculer le % d'avancement de l'axe
                  const axeProgress = calculateAxeProgress(axe);
                  
                  return (
                    <div key={aIdx} className="bg-slate-900/50 rounded-lg p-4 border border-indigo-500/20">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="text-lg font-bold text-indigo-300 mb-1">{axe.axe}</h5>
                          {axe.intention && (
                            <p className="text-xs text-gray-400 italic mb-2">{axe.intention}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                          {/* Cercle de progression pour l'axe */}
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Avancement</div>
                              <div className="text-sm font-bold text-indigo-300">{axeProgress}%</div>
                            </div>
                            <div className="w-12 h-12 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                              <svg className="w-9 h-9 transform -rotate-90" viewBox="0 0 24 24">
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  fill="none"
                                  stroke="rgba(16, 185, 129, 0.2)"
                                  strokeWidth="2"
                                />
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  fill="none"
                                  stroke="rgb(16, 185, 129)"
                                  strokeWidth="2"
                                  strokeDasharray={`${2 * Math.PI * 10 * (axeProgress / 100)} ${2 * Math.PI * 10}`}
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                          </div>
                          {totalEffortAxe > 0 && (
                            <div className="bg-indigo-500/20 border border-indigo-500/50 rounded-lg px-3 py-1.5">
                              <p className="text-xs text-gray-400 text-center">Total</p>
                              <p className="text-lg font-bold text-indigo-300 text-center">{totalEffortAxe}h</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-indigo-500/20">
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300">Livrable</th>
                              <th className="text-center py-2 px-3 text-xs font-bold text-indigo-300 w-32">Responsables</th>
                              <th className="text-center py-2 px-3 text-xs font-bold text-indigo-300 w-24">Priorité</th>
                              <th className="text-right py-2 px-3 text-xs font-bold text-indigo-300 w-24">Effort (h)</th>
                              <th className="text-center py-2 px-3 text-xs font-bold text-indigo-300 w-20">Complété</th>
                            </tr>
                          </thead>
                          <tbody>
                            {axe.livrables.map((livrable: any, lIdx: number) => {
                              const texte = typeof livrable === 'string' ? livrable : livrable.texte;
                              const effort = typeof livrable === 'string' ? 0 : livrable.effort || 0;
                              const priorite = typeof livrable === 'string' ? 'Moyenne' : livrable.priorite || 'Moyenne';
                              
                              // Liste des livrables complétés à 100%
                              const livrablesCompletes100 = [
                                'Déploiement PRIsME Employé pour 50% des employés',
                                'Cloner TactiCE (Projet) dans PRIsME avec structure complète',
                                'Gouvernance projet structurée (rôles, décisions, escalade) - pilotée par DB et EH',
                                'Onboarding intégré à PRIsME Employé avec checklist par rôle',
                                'RACI complet documenté et visible dans PRIsME'
                              ];
                              // Liste des livrables complétés à 75%
            const livrablesCompletes75 = [
              'Gestion documentaire 8 1/2 x 11 impeccable (PDF export optimisé)',
              'Automatisation génération documents (rapports, livrables)',
              'Tous les rôles ont leur PRIsME Employé complet et à jour',
              'Processus livraison PRIsME Client optimisé (workflow, checkpoints)',
              'Documentation processus par rôle (responsabilités, livrables, outils)',
              'Lien avec Shield / Decision Desk opérationnel (firewall mental)',
              'Format Decision Packet standardisé pour escalade vers Dany',
              'Rituels gouvernance documentés (2 réunions/mois, présence Dany 1x)',
              'Processus décision formalisé (critères, escalade, délais)'
            ];
                              // Liste des livrables complétés à 50%
                              const livrablesCompletes50 = [
                                'Formation gestionnaires sur leur responsabilité contenu départemental',
                                'Gestionnaires autonomes sur leur contenu départemental',
                                'Mise à jour régulière PRIsME Employé (mensuelle minimum)',
                                'Feedback boucle intégrée (suggestions, améliorations)',
                                'Intégration PRIsME Employé avec outils quotidiens (SSO, notifications)',
                                'Intégration projets TactiCE avec PRIsME Client (livraison traçable)',
                                'BOT PRIsME opérationnel (réponses FAQ, guidage processus)',
                                'Interface conversationnelle pour guider les Primes',
                                'Sécurité intégrée (classification, accès, audit)',
                                'Qualité 100% vérifiée avant livraison (checklist, validation)',
                                'Templates livraison PRIsME Client standardisés (structure, contenu)',
                                'Pages PRIsME Employé versionnées et mises à jour régulièrement',
                                'Métriques adoption PRIsME Employé (visites, temps onboarding, autonomie)',
                                'Règles d\'escalade claires (quand escalader, quand non)'
                              ];
                              // Liste des livrables complétés à 25%
                              const livrablesCompletes25 = [
                                'Automatisation tâches répétitives (création pages, notifications, rappels)',
                                'Amélioration continue processus (rétrospectives, ajustements)'
                              ];
                              const estComplete100 = livrablesCompletes100.includes(texte);
                              const estComplete75 = livrablesCompletes75.includes(texte);
                              const estComplete50 = livrablesCompletes50.includes(texte);
                              const estComplete25 = livrablesCompletes25.includes(texte);
                              
                              const prioriteColors: Record<string, string> = {
                                'Haute': 'bg-red-500/20 text-red-300 border-red-500/50',
                                'Moyenne': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
                                'Basse': 'bg-green-500/20 text-green-300 border-green-500/50'
                              };
                              
                              return (
                                <tr key={lIdx} className="border-b border-indigo-500/10 hover:bg-white/5">
                                  <td className="py-2 px-3">
                                    <div className="flex items-start gap-2 text-gray-300 text-sm">
                                      <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                                      <span>{texte}</span>
                                    </div>
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    {personnesAR.length > 0 ? (
                                      <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                        {personnesAR.map((personne, pIdx) => {
                                          const avatarSrc = avatarMap[personne.nom];
                                          return (
                                            <div key={pIdx} className="flex items-center gap-1" title={personne.nom}>
                                              {avatarSrc ? (
                                                avatarSrc === '$' || avatarSrc === '👤' ? (
                                                  <div className="w-6 h-6 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center text-xs font-bold text-indigo-300" title={personne.nom}>
                                                    {avatarSrc}
                                                  </div>
                                                ) : (
                                                  <img 
                                                    src={avatarSrc} 
                                                    alt={personne.nom} 
                                                    title={personne.nom}
                                                    className="w-6 h-6 rounded-full border-2 border-indigo-500/50 object-cover cursor-help"
                                                    onError={(e) => {
                                                      const target = e.target as HTMLImageElement;
                                                      target.style.display = 'none';
                                                    }}
                                                  />
                                                )
                                              ) : (
                                                <div className="w-6 h-6 rounded-full bg-indigo-500/20 border-2 border-indigo-500/50 flex items-center justify-center text-xs text-white cursor-help" title={personne.nom}>
                                                  {personne.nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                </div>
                                              )}
                                              <span className={`text-xs font-bold px-1 py-0.5 rounded cursor-help ${
                                                personne.role === 'A' 
                                                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50' 
                                                  : personne.role === 'R'
                                                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50'
                                                  : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50'
                                              }`} title={`${personne.nom} - ${personne.role === 'A' ? 'Accountable' : personne.role === 'R' ? 'Responsable' : 'Consulté'}`}>
                                                {personne.role}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-500">-</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    <span className={`text-xs font-bold px-2 py-1 rounded border ${prioriteColors[priorite] || prioriteColors['Moyenne']}`}>
                                      {priorite}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-right">
                                    {effort > 0 ? (
                                      <span className="text-sm font-semibold text-indigo-300">{effort}h</span>
                                    ) : (
                                      <span className="text-xs text-gray-500">-</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    {estComplete100 ? (
                                      <div className="flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center">
                                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                                        </div>
                                      </div>
                                    ) : estComplete75 ? (
                                      <div className="flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                                          {/* Cercle de progression à 75% */}
                                          <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 24 24">
                                            {/* Cercle de fond */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgba(16, 185, 129, 0.2)"
                                              strokeWidth="2"
                                            />
                                            {/* Arc de progression à 75% */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgb(16, 185, 129)"
                                              strokeWidth="2"
                                              strokeDasharray={`${2 * Math.PI * 10 * 0.75} ${2 * Math.PI * 10}`}
                                              strokeLinecap="round"
                                            />
                                          </svg>
                                        </div>
                                      </div>
                                    ) : estComplete50 ? (
                                      <div className="flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                                          {/* Cercle de progression à 50% */}
                                          <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 24 24">
                                            {/* Cercle de fond */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgba(16, 185, 129, 0.2)"
                                              strokeWidth="2"
                                            />
                                            {/* Arc de progression à 50% */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgb(16, 185, 129)"
                                              strokeWidth="2"
                                              strokeDasharray={`${2 * Math.PI * 10 * 0.5} ${2 * Math.PI * 10}`}
                                              strokeLinecap="round"
                                            />
                                          </svg>
                                        </div>
                                      </div>
                                    ) : estComplete25 ? (
                                      <div className="flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                                          {/* Cercle de progression à 25% */}
                                          <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 24 24">
                                            {/* Cercle de fond */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgba(16, 185, 129, 0.2)"
                                              strokeWidth="2"
                                            />
                                            {/* Arc de progression à 25% */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgb(16, 185, 129)"
                                              strokeWidth="2"
                                              strokeDasharray={`${2 * Math.PI * 10 * 0.25} ${2 * Math.PI * 10}`}
                                              strokeLinecap="round"
                                            />
                                          </svg>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-500">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          {totalEffortAxe > 0 && (
                            <tfoot>
                              <tr className="border-t-2 border-indigo-500/30 bg-indigo-500/10">
                                <td className="py-2 px-3 text-right font-bold text-indigo-300" colSpan={4}>
                                  Total {axe.axe}
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <span className="text-lg font-bold text-indigo-300">{totalEffortAxe}h</span>
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Focus PME */}
      <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-6 border-2 border-purple-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-purple-400" />
          Focus PME 2026
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <h5 className="text-sm font-bold text-purple-300 mb-2">~50 livrables structurants</h5>
            <p className="text-gray-300 text-sm">
              Livrables alignés avec les 10 axes PRIsME, priorisés selon l'impact business et la faisabilité technique.
            </p>
          </div>
          <div>
            <h5 className="text-sm font-bold text-purple-300 mb-2">20 no-brainers</h5>
            <p className="text-gray-300 text-sm">
              Actions rapides à haut impact : documentation, formations, intégrations simples, améliorations UX.
            </p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-purple-500/30">
          <h5 className="text-sm font-bold text-purple-300 mb-3">Éléments clés mis en évidence dans la roadmap :</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span><strong className="text-white">PRIsME Employé</strong> — Déploiement complet</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span><strong className="text-white">Outil projet intégré</strong> (inspiré PMBOK)</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span><strong className="text-white">Catalogue d'actifs</strong> — Recherche performante</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span><strong className="text-white">BOT PRIsME</strong> — Automatisation opérationnelle</span>
            </div>
          </div>
        </div>
      </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ONGLET 6 — GOUVERNANCE & RITUELS
// ============================================================================
function RituelsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Gouvernance & rituels
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Cadence, règles de décision et mécanismes de gouvernance
        </p>
      </div>

      {/* Cadence des rencontres */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-indigo-400" />
          Cadence des rencontres
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Comité PRIsME</strong> : 2 fois par mois (1er et 3e mardi, 10h-11h30)
              <p className="text-sm text-gray-400 mt-1">Animé par Francis Marquis (Facilitateur)</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Dany Bourassa</strong> : Présent 1 fois par mois (1er mardi uniquement)
              <p className="text-sm text-gray-400 mt-1">Effort décroissant : 20h/sem (maintenant) → 10h en mars → 5h en juin</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Ordre du jour</strong> : Distribué 48h avant, préparé par l'Owner (Éric Hains)
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Compte-rendu</strong> : Rédigé dans les 24h, partagé avec tous les membres
            </div>
          </li>
        </ul>
      </div>

      {/* Règles de décision */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-400" />
          Règles de décision
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions opérationnelles</strong> : Owner (<strong className="text-emerald-300">Éric Hains</strong>) décide, Comité consulté
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions stratégiques</strong> : PRIsME Master (Dany) décide, Comité consulté
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions techniques</strong> : Équipe technique décide, Owner (Éric) valide
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Consensus</strong> : Recherché pour les décisions impactantes, vote si nécessaire
            </div>
          </li>
        </ul>
      </div>

      {/* Canal direct vers Dany */}
      <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-6 border-2 border-purple-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-purple-400" />
          Canal direct vers Dany (PRIsME Master)
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Le canal direct vers Dany est <strong className="text-white">réservé aux rôles clés</strong> : Owner (Éric Hains) et Facilitateur (Francis Marquis).
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Pour toute demande nécessitant l'attention de Dany, utiliser le format <strong className="text-white">Decision Packet</strong> structuré :
        </p>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Contexte</strong> : Situation actuelle, enjeu, impact</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Options</strong> : Alternatives considérées avec avantages/inconvénients</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Recommandation</strong> : Option privilégiée avec justification</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Décision requise</strong> : Question claire et action attendue</span>
          </li>
        </ul>
        <p className="text-gray-300 leading-relaxed mt-4 text-sm italic">
          Ce format garantit une décision rapide et éclairée, tout en respectant le temps limité de Dany.
        </p>
      </div>

      {/* Quand escalader */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-6 h-6 text-yellow-400" />
          Quand escalader
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Impact organisationnel majeur</strong> : Changements affectant tous les Primes</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Investissement significatif</strong> : Dépenses &gt; 10k$ ou &gt; 40h de développement</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Conflit de vision</strong> : Désaccord sur la direction stratégique</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Risque réputationnel</strong> : Décisions affectant l'image de BZ</span>
          </li>
        </ul>
      </div>

      {/* Quand ne PAS escalader */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-emerald-400" />
          Quand ne PAS escalader
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions opérationnelles routinières</strong> : Owner (Éric) peut décider seul
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Corrections de bugs</strong> : Équipe technique gère
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Améliorations UX mineures</strong> : Owner (Éric) valide, pas besoin de Comité
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Documentation et formation</strong> : Processus standard, pas d'escalade
            </div>
          </li>
        </ul>
      </div>

      {/* Lien avec Shield / Decision Desk */}
      <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl p-6 border-2 border-indigo-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-400" />
          Lien conceptuel avec Shield / Decision Desk
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Les décisions stratégiques PRIsME sont documentées dans Shield (Decision Desk) pour traçabilité et alignement organisationnel. 
          <strong className="text-white"> Shield agit comme pare-feu mental</strong> : il structure les décisions importantes et évite la surcharge cognitive.
        </p>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Décisions majeures PRIsME → Shield Decision Desk (format Decision Packet)</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Roadmap PRIsME alignée avec roadmap Shield</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>KPI PRIsME intégrés aux tableaux de bord Shield</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Shield = pare-feu mental pour éviter la surcharge décisionnelle</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
